import React, { useState, useEffect, useRef, useCallback, useImperativeHandle, useMemo } from 'react';
import { toast } from 'sonner';
import { Trash2, ChevronDown, ChevronUp, Volume2, VolumeX, Plus, GitMerge } from 'lucide-react';

// TODO: Refactor duplicate code - Left and right page sections have near-identical code
// (block rendering, page headers, save logic, block update handlers)
// Consider extracting into reusable components/functions that accept isLeft parameter
import { settingsService } from '../services/SettingsService';
import { useSpreadState } from '../hooks/useSpreadState';
import { useSpreadData } from '../hooks/useSpreadData';
import InlinePageTypeEditor from './InlinePageTypeEditor';
import TextractBlockEditor from './TextractBlockEditor';
import { PAGE_TYPES, type PageData, type PageType, type EnhancedPageWordsData } from '../types/editing';
import { BLOCK_TYPES, type BlockData } from '../config/wordOverlayConfig';
import { useWordDataContext } from '../contexts/WordDataContext';
import { useAuth } from '../contexts/AuthContext';
import { createHash } from '../utils/hashUtils';
import { isAdjustedId, getNextAvailableId, extractOriginalIdInfo, findNthOccurrence } from '../utils/idCollisionHandler';

export interface ApprovalData {
  images?: boolean | string;
  words?: boolean | string;
  voice?: boolean | string;
  notes?: string;
}

interface SpreadEditorProps {
  isbn: string;
  approval?: ApprovalData;
  onApprovalChange: (approval: ApprovalData) => void;
  onReadMe?: () => void;
  onStop?: () => void;
  onReadFromWord?: (wordId: number | string, pageIndex: number, isLeftPage: boolean) => void;
  ttsActive?: boolean;
  ttsLoading?: boolean;
  ttsPaused?: boolean;
  currentLeftPage: number;
  currentRightPage: number;
  pages: PageData[];
  onShowOverlaysChange?: (showOverlays: boolean) => void;
  onPageTypeChange?: (pageIndex: number, pageType: PageType) => void;
  onStateChange?: (state: { hasChanges: boolean; isSaving: boolean; saveButtonText: string; saveButtonTitle: string; currentApproval?: ApprovalData }) => void;
}

export interface SpreadEditorRef {
  setEditMode: (mode: 'text' | 'resize' | 'move' | 'delete-word' | 'delete-block' | null) => void;
  handleSave: () => Promise<void>;
  getHasChanges: () => boolean;
  getIsSaving: () => boolean;
  getSaveButtonText: () => string;
  getSaveButtonTitle: () => string;
  getCurrentApproval: () => ApprovalData | undefined;
  handleNotesChange: (notes: string) => void;
  getShowOverlays: () => boolean;
  getShowBlockBg: () => boolean;
  getInvertPage: () => boolean;
  handleToggleOverlays: () => void;
  handleToggleBlockBg: () => void;
  handleToggleInvert: () => void;
  handleAddBlock: (pageIndex: number | null, isLeft: boolean) => void;
  getLeftPageIndex: () => number | null;
  getRightPageIndex: () => number | null;
  cancelMerge: () => boolean; // Returns true if merge was active and canceled
  getAudioService: () => 'azure' | 'elevenlabs' | 'curioso-custom';
  getSelectedVoice: () => string;
  getElevenlabsModel: () => string;
  isBlockEditing: () => boolean; // Returns true if any block is in edit mode
  closeAllBlockEditors: () => void; // Closes all block editors
}

const SpreadEditor = React.forwardRef<SpreadEditorRef, SpreadEditorProps>(({ 
  isbn, 
  approval, 
  onApprovalChange, 
  onReadMe, 
  onStop,
  onReadFromWord,
  ttsActive = false, 
  ttsLoading = false, 
  ttsPaused = false,
  currentLeftPage,
  currentRightPage,
  pages,
  onShowOverlaysChange,
  onPageTypeChange,
  onStateChange
}, ref) => {
  const [currentApproval, setCurrentApproval] = useState<ApprovalData>(approval || {});
  const [originalApprovalNotes, setOriginalApprovalNotes] = useState<string>(approval?.notes || '');
  const lastIsbnRef = useRef<string>(isbn);
  const isInternalChangeRef = useRef<boolean>(false);
  
  // Update original notes only when ISBN changes (switching books), not when approval prop updates from our own changes
  useEffect(() => {
    if (isbn !== lastIsbnRef.current) {
      // ISBN changed, this is a new book - reset original notes
      setOriginalApprovalNotes(approval?.notes || '');
      setCurrentApproval(approval || {});
      lastIsbnRef.current = isbn;
      isInternalChangeRef.current = false;
    } else if (!isInternalChangeRef.current && approval) {
      // Approval changed from outside (not our own change) - sync currentApproval but don't reset original
      setCurrentApproval(approval);
    }
    // Reset the flag after processing
    isInternalChangeRef.current = false;
  }, [isbn, approval]);
  
  const containerRef = useRef<HTMLDivElement>(null);

  // Block reordering state
  const [isReorderingBlock, setIsReorderingBlock] = useState(false);
  const [reorderDragBlockId, setReorderDragBlockId] = useState<number | string | null>(null);
  const [reorderDragIsLeft, setReorderDragIsLeft] = useState<boolean>(true);
  const [reorderDragStartY, setReorderDragStartY] = useState<number | null>(null);
  const [reorderDragOffset, setReorderDragOffset] = useState<number>(0);
  const reorderDragOffsetRef = useRef<number>(0);
  const blockItemRefs = useRef<Map<string, HTMLDivElement>>(new Map()); // Key format: `${pageIndex}-${blockId}`

  // Block merge state
  const [mergeSourceBlockId, setMergeSourceBlockId] = useState<number | string | null>(null);
  const [mergeSourcePageIndex, setMergeSourcePageIndex] = useState<number | null>(null);

  // Overlay and invert controls
  const [showOverlays, setShowOverlays] = useState(true);
  const [invertPage, setInvertPage] = useState(false);
  const [showBlockBg, setShowBlockBg] = useState(true);

  // Audio service state (curioso-custom only when book.audio_inside is true)
  const [audioService, setAudioService] = useState<'azure' | 'elevenlabs' | 'curioso-custom'>('azure');
  const [selectedVoice, setSelectedVoice] = useState<string>('default');
  const [elevenlabsVoices, setElevenlabsVoices] = useState<Array<{ name: string; voice_id?: string }>>([]);
  const [elevenlabsModel, setElevenlabsModel] = useState<string>('default');
  const [voiceSettingsChanged, setVoiceSettingsChanged] = useState(false);
  const [originalVoiceService, setOriginalVoiceService] = useState<string | null>(null);
  const [originalVoiceName, setOriginalVoiceName] = useState<string | null>(null);
  const [loadingElevenlabsVoices, setLoadingElevenlabsVoices] = useState(false);
  const hasUserChangedAudioServiceRef = useRef(false); // Track if user has manually changed the service
  const [bookCharCount, setBookCharCount] = useState<number | null>(null);
  const [bookCostEstimate, setBookCostEstimate] = useState<number | null>(null);
  const [ttsChars, setTtsChars] = useState<number | null>(null);
  const [ttsCostAzure, setTtsCostAzure] = useState<number | null>(null);
  const [ttsCostElevenlabs, setTtsCostElevenlabs] = useState<number | null>(null);
  
  // Collapse state for page sections (load from settings)
  const [leftPageCollapsed, setLeftPageCollapsed] = useState(() => 
    settingsService.getSpreadEditorLeftPageCollapsed()
  );
  const [rightPageCollapsed, setRightPageCollapsed] = useState(() => 
    settingsService.getSpreadEditorRightPageCollapsed()
  );

  useEffect(() => {
    settingsService.setSpreadEditorLeftPageCollapsed(leftPageCollapsed);
  }, [leftPageCollapsed]);

  useEffect(() => {
    settingsService.setSpreadEditorRightPageCollapsed(rightPageCollapsed);
  }, [rightPageCollapsed]);

  // Initialize block bg on mount
  useEffect(() => {
    if (showBlockBg) {
      document.body.classList.add('show-block-bg');
    }
    return () => {
      // Cleanup on unmount
      document.body.classList.remove('show-block-bg');
    };
  }, []); // Only run on mount

  // Spread state management
  const { activeSpread, setActiveSpread } = useSpreadState({
    currentLeftPage,
    currentRightPage,
    pages,
  });

  // Load word data for both pages
  const spreadData = useSpreadData({
    isbn,
    activeSpread,
    setActiveSpread,
  });

  // Get WordDataContext to sync block changes
  const { state: wordDataState, actions: wordDataActions } = useWordDataContext();

  const { accessLevel } = useAuth();
  const isFullAccessUser = accessLevel === 'FULL_ACCESS';

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [spineData, setSpineData] = useState<any>(null);
  const [originalSpineHash, setOriginalSpineHash] = useState<string | null>(null);

  // Azure voices list
  const AZURE_VOICES = [
    'default',
    'en-US-AvaMultilingualNeural',
    'en-US-AndrewMultilingualNeural',
    'en-US-EmmaMultilingualNeural',
    'en-US-BrianMultilingualNeural',
    'en-US-AvaNeural',
    'en-US-AndrewNeural',
    'en-US-EmmaNeural',
    'en-US-BrianNeural',
    'en-US-JennyNeural',
    'en-US-GuyNeural',
    'en-US-AriaNeural',
    'en-US-DavisNeural',
    'en-US-JaneNeural',
    'en-US-JasonNeural',
    'en-US-KaiNeural',
    'en-US-LunaNeural',
    'en-US-SaraNeural',
    'en-US-TonyNeural',
    'en-US-NancyNeural',
    'en-US-CoraMultilingualNeural',
    'en-US-ChristopherMultilingualNeural',
    'en-US-BrandonMultilingualNeural',
    'en-US-AmberNeural',
    'en-US-AnaNeural',
    'en-US-AshleyNeural',
    'en-US-BrandonNeural',
    'en-US-ChristopherNeural',
    'en-US-CoraNeural',
    'en-US-ElizabethNeural',
    'en-US-EricNeural',
    'en-US-JacobNeural',
    'en-US-JennyMultilingualNeural',
    'en-US-MichelleNeural',
    'en-US-MonicaNeural',
    'en-US-RogerNeural',
    'en-US-RyanMultilingualNeural',
    'en-US-SteffanNeural',
  ];

  // Load ElevenLabs voices from API
  const loadElevenlabsVoices = useCallback(async () => {
    if (loadingElevenlabsVoices || elevenlabsVoices.length > 0) return; // Already loaded or loading
    
    setLoadingElevenlabsVoices(true);
    try {
      // Determine server endpoint (use production for now, can be made configurable)
      const server = 'production'; // Could be made configurable
      const baseUrl = server === 'production' 
        ? 'https://audio.getcurioso.com' 
        : 'http://localhost:3000';
      
      const response = await fetch(`${baseUrl}/voices`);
      if (!response.ok) {
        throw new Error(`Failed to fetch voices: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.elevenlabs && Array.isArray(data.elevenlabs)) {
        setElevenlabsVoices(data.elevenlabs);
      }
    } catch (error) {
      console.error('Error loading ElevenLabs voices:', error);
      toast.error('Failed to load ElevenLabs voices');
    } finally {
      setLoadingElevenlabsVoices(false);
    }
  }, [loadingElevenlabsVoices, elevenlabsVoices.length]);

  // Load spine data
  useEffect(() => {
    const loadSpineData = async () => {
      try {
        const { FolderService } = await import('../services/FolderService');
        const folder = FolderService.getFolder();
        if (!folder) {
          console.error('[SPREAD EDITOR] No folder selected, cannot load spine data');
          return;
        }
        const { authenticatedFetch } = await import('../utils/api');
        const url = FolderService.addFolderToUrl(`/api/books/${isbn}/spine`);
        const response = await authenticatedFetch(url);
        if (response.ok) {
          const data = await response.json();
          setSpineData(data);
          setOriginalSpineHash(createHash(data));
          
          // Load voice settings from spine (only if user hasn't manually changed them)
          if (!hasUserChangedAudioServiceRef.current) {
            const voiceService = data.book?.voice_service || 'azure';
            const voiceName = data.book?.voice_name || 'default';
            const service: 'azure' | 'elevenlabs' | 'curioso-custom' =
              voiceService === 'curioso-custom' ? 'curioso-custom'
              : voiceService === 'elevenlabs' ? 'elevenlabs' : 'azure';
            setAudioService(service);
            setSelectedVoice(voiceService === 'curioso-custom' ? 'default' : voiceName);
            setOriginalVoiceService(voiceService);
            setOriginalVoiceName(voiceService === 'curioso-custom' ? 'default' : voiceName);
            setVoiceSettingsChanged(false);
            
            if (voiceService === 'elevenlabs') {
              loadElevenlabsVoices();
            }
          } else {
            const voiceService = data.book?.voice_service || 'azure';
            const voiceName = data.book?.voice_name || 'default';
            setOriginalVoiceService(voiceService);
            setOriginalVoiceName(voiceName);
            const hasChanged = audioService !== voiceService || selectedVoice !== voiceName;
            setVoiceSettingsChanged(hasChanged);
          }
        }
      } catch (error) {
        console.error('[SPREAD EDITOR] Failed to load spine data:', error);
      }
    };
    if (isbn) {
      loadSpineData();
    }
    // Reset the flag when ISBN changes (new book loaded)
    hasUserChangedAudioServiceRef.current = false;
  }, [isbn]); // Removed loadElevenlabsVoices from dependencies to prevent re-running when voices load

  useEffect(() => {
    setCurrentApproval(approval || {});
  }, [approval]);


  // Reset invert state when spread changes
  const prevLeftPageRef = useRef<number | null>(null);
  const prevRightPageRef = useRef<number | null>(null);
  useEffect(() => {
    const leftChanged = prevLeftPageRef.current !== null && prevLeftPageRef.current !== activeSpread.leftPageIndex;
    const rightChanged = prevRightPageRef.current !== null && prevRightPageRef.current !== activeSpread.rightPageIndex;
    
    if (leftChanged || rightChanged) {
      // Reset filter on previous pages if they were inverted
      if (invertPage) {
        if (leftChanged && prevLeftPageRef.current !== null) {
          const pageElements = document.querySelectorAll(`[data-page-index="${prevLeftPageRef.current}"]`);
          pageElements.forEach(page => {
            const img = page.querySelector('img');
            if (img) {
              img.style.filter = '';
            }
          });
        }
        if (rightChanged && prevRightPageRef.current !== null) {
          const pageElements = document.querySelectorAll(`[data-page-index="${prevRightPageRef.current}"]`);
          pageElements.forEach(page => {
            const img = page.querySelector('img');
            if (img) {
              img.style.filter = '';
            }
          });
        }
      }
      // Reset invert state when spread changes
      setInvertPage(false);
    }
    
    prevLeftPageRef.current = activeSpread.leftPageIndex;
    prevRightPageRef.current = activeSpread.rightPageIndex;
  }, [activeSpread.leftPageIndex, activeSpread.rightPageIndex, invertPage]);

  const handleToggleOverlays = useCallback(() => {
    const newShowOverlays = !showOverlays;
    setShowOverlays(newShowOverlays);
    
    // Notify parent component
    onShowOverlaysChange?.(newShowOverlays);
    
    // Update all overlay containers on the page (applies to both left and right)
    // If new state is true (show), remove hide class; if false (hide), add hide class
    const overlayContainers = document.querySelectorAll('.word-overlay-container');
    overlayContainers.forEach(container => {
      if (newShowOverlays) {
        container.classList.remove('hide-overlays');
      } else {
        container.classList.add('hide-overlays');
      }
    });
  }, [showOverlays, onShowOverlaysChange]);

  // Listen for enableOverlays event from BlockOverlay
  useEffect(() => {
    const handleEnableOverlays = () => {
      if (!showOverlays) {
        handleToggleOverlays();
      }
    };
    
    window.addEventListener('enableOverlays', handleEnableOverlays);
    return () => {
      window.removeEventListener('enableOverlays', handleEnableOverlays);
    };
  }, [showOverlays, handleToggleOverlays]);

  // Helper function to set edit mode and enable overlays if needed
  const handleSetEditMode = useCallback((mode: 'text' | 'resize' | 'move' | 'delete-word' | 'delete-block' | null) => {
    // If overlays are off, turn them on first
    if (!showOverlays) {
      handleToggleOverlays();
    }
    // Set the edit mode
    wordDataActions.setEditMode(mode);
  }, [showOverlays, handleToggleOverlays, wordDataActions]);


  const handleToggleBlockBg = useCallback(() => {
    const newShowBlockBg = !showBlockBg;
    setShowBlockBg(newShowBlockBg);
    
    // Add/remove class on body to apply block backgrounds globally
    if (newShowBlockBg) {
      document.body.classList.add('show-block-bg');
    } else {
      document.body.classList.remove('show-block-bg');
    }
  }, [showBlockBg]);

  const handleToggleInvert = useCallback(() => {
    const newInvertState = !invertPage;
    setInvertPage(newInvertState);
    
    // Apply filter to both left and right pages
    const pageIndices: (number | null)[] = [
      activeSpread.leftPageIndex,
      activeSpread.rightPageIndex
    ].filter((idx): idx is number => idx !== null);
    
    pageIndices.forEach(pageIndex => {
      const pageElements = document.querySelectorAll(`[data-page-index="${pageIndex}"]`);
      pageElements.forEach(page => {
        const img = page.querySelector('img');
        if (img) {
          if (newInvertState) {
            img.style.filter = 'invert(1)';
          } else {
            img.style.filter = '';
          }
        }
      });
    });
  }, [invertPage, activeSpread.leftPageIndex, activeSpread.rightPageIndex]);

  const getImagesStatus = () => {
    const value = currentApproval.images;
    if (value === true || value === 'true') return 'approved';
    if (value === false || value === 'false') return 'fail';
    return 'unapproved';
  };

  const getWordsStatus = () => {
    const value = currentApproval.words;
    if (value === true || value === 'true') return 'approved';
    if (value === false || value === 'false') return 'fail';
    if (value === 'auto') return 'auto';
    return 'unapproved';
  };

  const getVoiceStatus = () => {
    const value = currentApproval.voice;
    if (value === true || value === 'true') return 'added';
    return 'not-chosen';
  };

  const handleApprovalClick = async (type: 'images' | 'words' | 'voice', newValue: boolean | string) => {
    const updatedApproval = { ...currentApproval, [type]: newValue };
    setCurrentApproval(updatedApproval);
    isInternalChangeRef.current = true; // Mark this as our own change
    onApprovalChange(updatedApproval);
  };

  const handleNotesChange = useCallback((newNotes: string) => {
    const updatedApproval = { ...currentApproval, notes: newNotes };
    setCurrentApproval(updatedApproval);
    // Only update local state - save happens when save button is clicked
  }, [currentApproval]);

  // Handle audio service change (Azure / ElevenLabs buttons)
  const handleAudioServiceChange = useCallback((service: 'azure' | 'elevenlabs') => {
    hasUserChangedAudioServiceRef.current = true;
    setAudioService(service);
    if (service === 'azure') {
      setSelectedVoice('default');
    } else {
      setSelectedVoice('');
      if (elevenlabsVoices.length === 0) {
        loadElevenlabsVoices();
      }
    }
    if (service === 'elevenlabs') {
      setVoiceSettingsChanged(false);
    } else {
      const hasChanged = service !== originalVoiceService || 'default' !== originalVoiceName;
      setVoiceSettingsChanged(hasChanged);
    }
  }, [originalVoiceService, originalVoiceName, elevenlabsVoices.length, loadElevenlabsVoices]);

  // Handle "Use custom voice" toggle (only when book.audio_inside is true)
  const handleUseCustomVoiceChange = useCallback((checked: boolean) => {
    hasUserChangedAudioServiceRef.current = true;
    if (checked) {
      setAudioService('curioso-custom');
      setSelectedVoice('default');
      setVoiceSettingsChanged(true);
    } else {
      const prevService = originalVoiceService === 'curioso-custom' ? 'azure' : (originalVoiceService || 'azure');
      const newVoice = prevService === 'elevenlabs' ? (originalVoiceName || '') : 'default';
      setAudioService(prevService === 'elevenlabs' ? 'elevenlabs' : 'azure');
      setSelectedVoice(newVoice);
      setVoiceSettingsChanged(
        prevService === 'elevenlabs' && newVoice === '' ? false : prevService !== originalVoiceService || newVoice !== (originalVoiceName || '')
      );
      if (prevService === 'elevenlabs' && elevenlabsVoices.length === 0) {
        loadElevenlabsVoices();
      }
    }
  }, [originalVoiceService, originalVoiceName, elevenlabsVoices.length, loadElevenlabsVoices]);

  // Handle voice change
  const handleVoiceChange = useCallback((voice: string) => {
    setSelectedVoice(voice);
    // Check if settings changed
    const hasChanged = audioService !== originalVoiceService || voice !== originalVoiceName;
    setVoiceSettingsChanged(hasChanged);
  }, [audioService, originalVoiceService, originalVoiceName]);

  // Model is always "default" for ElevenLabs - no handler needed

  // Calculate total book character count and cost estimate
  const calculateBookCharCount = useCallback(async () => {
    if (!isbn || !pages || pages.length === 0) {
      setBookCharCount(null);
      setBookCostEstimate(null);
      setTtsChars(null);
      setTtsCostAzure(null);
      setTtsCostElevenlabs(null);
      return;
    }

    try {
      const { FolderService } = await import('../services/FolderService');
      const { authenticatedFetch } = await import('../utils/api');

      // Helper function to process a page
      const processPage = async (pageFilename: string): Promise<string> => {
        try {
          const pageUrl = FolderService.getPageJsonUrl(isbn, pageFilename);
          const response = await authenticatedFetch(pageUrl);
          if (response.ok) {
            const wordData = await response.json();
            if (wordData.blocks && Array.isArray(wordData.blocks)) {
              const readBlocks = wordData.blocks
                .filter((block: any) => block.readBlock === true)
                .sort((a: any, b: any) => (a.blockReadOrder || 0) - (b.blockReadOrder || 0));
              
              const text = readBlocks
                .map((block: any, index: number) => {
                  const wordsText = block.words?.map((word: any) => word.text).join(' ') || '';
                  // Add . if blockType changes from previous block
                  if (index > 0 && readBlocks[index - 1].blockType !== block.blockType) {
                    return '. ' + wordsText;
                  }
                  return wordsText;
                })
                .join(' ')
                .trim();
              
              return text;
            }
          }
        } catch (error) {
          console.error(`Error loading page word data:`, error);
        }
        return '';
      };

      // Process all pages that have enhanced text extraction
      const pageTexts = await Promise.all(
        pages
          .filter(page => page.words && (page.text_extracted === 'enhanced' || page.text_extracted === true))
          .map(page => processPage(page.words))
      );

      // Combine all page texts
      let totalText = '';
      let lastBlockType: string | null = null;
      
      for (const pageText of pageTexts) {
        if (pageText) {
          // Add space between pages, or period if needed
          if (totalText && lastBlockType) {
            // Simple heuristic: add period if text doesn't end with punctuation
            const needsPeriod = !totalText.match(/[.!?]$/);
            totalText += needsPeriod ? '. ' : ' ';
          }
          totalText += pageText;
        }
      }

      const charCount = totalText.length;
      setBookCharCount(charCount);
      setTtsChars(charCount);

      // Calculate cost estimates for both services
      // Azure TTS: ~$15 per million characters
      // ElevenLabs Creator: $0.30 per 1000 characters
      const costAzure = (charCount / 1_000_000) * 15; // $15 per million chars
      const costElevenlabs = (charCount / 1000) * 0.30; // $0.30 per 1000 chars (Creator plan)
      
      setTtsCostAzure(costAzure);
      setTtsCostElevenlabs(costElevenlabs);

      // Set the cost estimate based on current service
      let costEstimate = 0;
      if (audioService === 'azure') {
        costEstimate = costAzure;
      } else if (audioService === 'elevenlabs') {
        costEstimate = costElevenlabs;
      }
      
      setBookCostEstimate(costEstimate);
    } catch (error) {
      console.error('Error calculating book character count:', error);
      setBookCharCount(null);
      setBookCostEstimate(null);
      setTtsChars(null);
      setTtsCostAzure(null);
      setTtsCostElevenlabs(null);
    }
  }, [isbn, pages, audioService]);

  // Save voice settings to spine
  const handleApplyVoiceSettings = useCallback(async () => {
    if (!isbn || !spineData) return;
    
    try {
      const { FolderService } = await import('../services/FolderService');
      const folder = FolderService.getFolder();
      if (!folder) {
        throw new Error('Folder is required but not set');
      }
      
      // Update spine data with voice settings
      const updatedSpineData = {
        ...spineData,
        book: {
          ...spineData.book,
          voice_service: audioService,
          voice_name: selectedVoice,
        }
      };
      
      const { authenticatedPut } = await import('../utils/api');
      const spineUrl = FolderService.addFolderToUrl(`/api/books/${isbn}/spine`);
      const response = await authenticatedPut(spineUrl, {
        isbn: isbn,
        data: updatedSpineData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Failed to save voice settings: ${response.statusText} - ${errorData.error || 'Unknown error'}`);
      }
      
      // Update local state
      setSpineData(updatedSpineData);
      setOriginalSpineHash(createHash(updatedSpineData));
      setOriginalVoiceService(audioService);
      setOriginalVoiceName(selectedVoice);
      setVoiceSettingsChanged(false);
      
      // Set voice approval to 'added' (true)
      const updatedApproval = { ...currentApproval, voice: true };
      setCurrentApproval(updatedApproval);
      isInternalChangeRef.current = true;
      onApprovalChange(updatedApproval);
      
      toast.success('Voice settings saved to book');
      
      // Recalculate and save TTS metadata after voice settings are saved
      await calculateBookCharCount();
    } catch (error) {
      console.error('Error saving voice settings:', error);
      toast.error(`Failed to save voice settings: ${error instanceof Error ? error.message : String(error)}`);
    }
  }, [isbn, spineData, audioService, selectedVoice, currentApproval, onApprovalChange, calculateBookCharCount]);

  // Save TTS metadata to spine.json
  const saveTtsMetadata = useCallback(async () => {
    if (!isbn || !spineData || ttsChars === null) return;
    
    try {
      const { FolderService } = await import('../services/FolderService');
      const folder = FolderService.getFolder();
      if (!folder) {
        throw new Error('Folder is required but not set');
      }

      // Check if values need to be saved (not present or different)
      const currentTtsChars = spineData.book?.tts_chars;
      const currentTtsCostAzure = spineData.book?.tts_cost_azure;
      const currentTtsCostElevenlabs = spineData.book?.tts_cost_elevenlabs;
      
      const needsSave = 
        currentTtsChars !== ttsChars ||
        currentTtsCostAzure !== ttsCostAzure ||
        currentTtsCostElevenlabs !== ttsCostElevenlabs;

      if (!needsSave) return;

      // Update spine data with TTS metadata
      const updatedSpineData = {
        ...spineData,
        book: {
          ...spineData.book,
          tts_chars: ttsChars,
          tts_cost_azure: ttsCostAzure,
          tts_cost_elevenlabs: ttsCostElevenlabs,
        }
      };
      
      const { authenticatedPut } = await import('../utils/api');
      const spineUrl = FolderService.addFolderToUrl(`/api/books/${isbn}/spine`);
      const response = await authenticatedPut(spineUrl, {
        isbn: isbn,
        data: updatedSpineData,
        skipSyncStatusReset: true
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Failed to save TTS metadata: ${response.statusText} - ${errorData.error || 'Unknown error'}`);
      }
      
      // Update local state
      setSpineData(updatedSpineData);
      setOriginalSpineHash(createHash(updatedSpineData));
    } catch (error) {
      console.error('Error saving TTS metadata:', error);
      // Don't show toast for this - it's a background operation
    }
  }, [isbn, spineData, ttsChars, ttsCostAzure, ttsCostElevenlabs]);

  // Calculate character count when book or service changes
  useEffect(() => {
    calculateBookCharCount();
  }, [calculateBookCharCount]);

  // Save TTS metadata when it's calculated and different from spine
  useEffect(() => {
    if (ttsChars !== null) {
      saveTtsMetadata();
    }
  }, [ttsChars, ttsCostAzure, ttsCostElevenlabs, saveTtsMetadata]);

  // Track if notes have changed to activate save button (book-level change)
  const hasBookChanges = (currentApproval?.notes || '') !== originalApprovalNotes;

  // Notify parent of state changes for TopEditor
  useEffect(() => {
    if (onStateChange) {
      const saveButtonText = isSaving 
        ? 'Saving...' 
        : activeSpread.leftHasChanges && activeSpread.rightHasChanges
          ? 'Save Both Pages'
          : activeSpread.leftHasChanges || activeSpread.rightHasChanges || hasBookChanges
            ? 'Save Changes'
            : 'No Changes';
      const saveButtonTitle = isSaving 
        ? 'Saving...' 
        : activeSpread.leftHasChanges && activeSpread.rightHasChanges
          ? 'Save changes to both pages'
          : activeSpread.leftHasChanges
            ? 'Save changes to left page'
            : activeSpread.rightHasChanges
              ? 'Save changes to right page'
              : hasBookChanges
                ? 'Save notes'
                : 'No changes to save';
      onStateChange({
        hasChanges: activeSpread.leftHasChanges || activeSpread.rightHasChanges || hasBookChanges,
        isSaving,
        saveButtonText,
        saveButtonTitle,
        currentApproval,
      });
    }
  }, [activeSpread.leftHasChanges, activeSpread.rightHasChanges, hasBookChanges, isSaving, currentApproval, onStateChange]);

  const imagesStatus = getImagesStatus();
  const wordsStatus = getWordsStatus();
  const voiceStatus = getVoiceStatus();

  // Get next value for cycling through states
  const getNextImagesValue = () => {
    if (imagesStatus === 'unapproved') return true;  // unapproved → approved
    if (imagesStatus === 'approved') return false;    // approved → fail
    return '';  // fail → unapproved
  };

  const getNextWordsValue = () => {
    if (wordsStatus === 'unapproved' || wordsStatus === 'auto') return true;   // unapproved/auto → approved
    if (wordsStatus === 'approved') return false;   // approved → fail
    return '';  // fail → unapproved
  };

  const getNextVoiceValue = () => {
    if (voiceStatus === 'not-chosen') return true;   // not-chosen → added
    return '';  // added → not-chosen
  };

  // Base button style shared by all buttons (matching TopEditor)
  const baseButtonStyle: React.CSSProperties = {
    border: '1px solid var(--border-primary)',
    borderRadius: '4px',
    fontSize: '11px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    transition: 'all 0.2s ease',
  };

  // Helper function to create button style with overrides
  const getButtonStyle = (overrides?: React.CSSProperties): React.CSSProperties => {
    return { ...baseButtonStyle, ...overrides };
  };

  // PageType display helpers (same as PageEditor)
  const getPageTypeDisplay = useCallback((pageType?: string) => {
    if (!pageType || pageType.trim() === '') return '';
    return pageType.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }, []);

  const getPageTypeColor = useCallback((pageType?: string) => {
    const type = pageType && pageType.trim() !== '' ? pageType : 'unknown';
    const colorMap: { [key: string]: string } = {
      'cover': '#e74c3c',
      'title': '#3498db',
      'content': '#2ecc71',
      'copyright': '#9b59b6',
      'table-of-contents': '#f39c12',
      'index': '#1abc9c',
      'multi': '#34495e',
      'back-page': '#95a5a6',
      'back-cover': '#e67e22',
      'blank-page': '#bdc3c7',
      'glossary': '#8e44ad',
      'bibliography': '#16a085',
      'dedication': '#d35400',
      'epigraph': '#7f8c8d',
      'foreword': '#27ae60',
      'preface': '#2980b9',
      'acknowledgments': '#c0392b',
      'appendix': '#8e44ad',
      'notes': '#f1c40f',
      'about-author': '#e67e22',
      'advertisement': '#e91e63',
      'abstract': '#607d8b',
      'abbreviations': '#795548',
      'chronology': '#009688',
      'maps': '#4caf50',
      'character-list': '#ff9800',
      'genealogy': '#673ab7',
      'pronunciation': '#ff5722',
      'other-non-content': '#9e9e9e',
      'unknown': '#cccccc'
    };
    return colorMap[type] || '#cccccc';
  }, []);

  // Get page data directly from pages prop
  const leftPageDataFromProps = activeSpread.leftPageIndex !== null 
    ? pages.find(page => page['page-index'] === activeSpread.leftPageIndex) || null
    : null;
  const rightPageDataFromProps = activeSpread.rightPageIndex !== null
    ? pages.find(page => page['page-index'] === activeSpread.rightPageIndex) || null
    : null;

  // Handle PageType changes - update pages prop directly
  const handleLeftPageTypeChange = useCallback((newPageType: PageType) => {
    if (activeSpread.leftPageIndex !== null && onPageTypeChange) {
      onPageTypeChange(activeSpread.leftPageIndex, newPageType);
      // Change detection will happen automatically via the useEffect that calls checkPageHasChanges
    }
  }, [activeSpread.leftPageIndex, onPageTypeChange]);

  const handleRightPageTypeChange = useCallback((newPageType: PageType) => {
    if (activeSpread.rightPageIndex !== null && onPageTypeChange) {
      onPageTypeChange(activeSpread.rightPageIndex, newPageType);
      // Change detection will happen automatically via the useEffect that calls checkPageHasChanges
    }
  }, [activeSpread.rightPageIndex, onPageTypeChange]);

  // Handle block updates for left page
  const handleLeftBlockUpdate = useCallback((updatedBlock: BlockData) => {
    if (activeSpread.leftPageIndex === null) return;
    
    // Update the entire block (including words) in the context
    const currentBlocks = wordDataState.blocksByPage.get(activeSpread.leftPageIndex) || [];
    const updatedBlocks = currentBlocks.map(block => {
      const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
      const updatedBlockIdNum = typeof updatedBlock.blockId === 'string' ? parseInt(updatedBlock.blockId, 10) : updatedBlock.blockId;
      if (blockIdNum === updatedBlockIdNum) {
        return updatedBlock; // Replace entire block to preserve word changes
      }
      return block;
    });
    
    // Update all blocks for this page to preserve the word changes
    wordDataActions.updatePageBlocks(activeSpread.leftPageIndex, updatedBlocks);
    
    // Change detection will happen automatically via the useEffect that calls checkPageHasChanges
  }, [activeSpread.leftPageIndex, wordDataState.blocksByPage, wordDataActions]);
  
  // Handle block updates for right page
  const handleRightBlockUpdate = useCallback((updatedBlock: BlockData) => {
    if (activeSpread.rightPageIndex === null) return;
    
    // Update the entire block (including words) in the context
    const currentBlocks = wordDataState.blocksByPage.get(activeSpread.rightPageIndex) || [];
    const updatedBlocks = currentBlocks.map(block => {
      const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
      const updatedBlockIdNum = typeof updatedBlock.blockId === 'string' ? parseInt(updatedBlock.blockId, 10) : updatedBlock.blockId;
      if (blockIdNum === updatedBlockIdNum) {
        return updatedBlock; // Replace entire block to preserve word changes
      }
      return block;
    });
    
    // Update all blocks for this page to preserve the word changes
    wordDataActions.updatePageBlocks(activeSpread.rightPageIndex, updatedBlocks);
    
    // Change detection will happen automatically via the useEffect that calls checkPageHasChanges
  }, [activeSpread.rightPageIndex, wordDataState.blocksByPage, wordDataActions]);

  // Block reordering handlers (for drag handle in list)
  const handleBlockReorderMouseDown = (e: React.MouseEvent, blockId: number | string, isLeft: boolean) => {
    e.preventDefault();
    e.stopPropagation();
    setIsReorderingBlock(true);
    setReorderDragBlockId(blockId);
    setReorderDragIsLeft(isLeft);
    setReorderDragStartY(e.clientY);
    setReorderDragOffset(0);
    reorderDragOffsetRef.current = 0;
  };

  const handleBlockReorderMouseMove = useCallback((e: MouseEvent) => {
    if (!isReorderingBlock || !reorderDragBlockId || reorderDragStartY === null) return;

    const currentY = e.clientY;
    const deltaY = currentY - reorderDragStartY;
    setReorderDragOffset(deltaY);
    reorderDragOffsetRef.current = deltaY;
  }, [isReorderingBlock, reorderDragBlockId, reorderDragStartY]);

  const handleBlockReorderMouseUp = useCallback((e: MouseEvent) => {
    if (!isReorderingBlock || !reorderDragBlockId || reorderDragStartY === null) {
      setIsReorderingBlock(false);
      setReorderDragBlockId(null);
      setReorderDragIsLeft(true);
      setReorderDragStartY(null);
      setReorderDragOffset(0);
      reorderDragOffsetRef.current = 0;
      return;
    }

    const pageIndex = reorderDragIsLeft ? activeSpread.leftPageIndex : activeSpread.rightPageIndex;
    if (pageIndex === null) {
      setIsReorderingBlock(false);
      setReorderDragBlockId(null);
      setReorderDragStartY(null);
      setReorderDragOffset(0);
      reorderDragOffsetRef.current = 0;
      return;
    }

    // Get blocks sorted by blockReadOrder for finding indices
    const sortedBlocks = [...(wordDataState.blocksByPage.get(pageIndex) || [])]
      .sort((a, b) => (a.blockReadOrder || 0) - (b.blockReadOrder || 0));
    
    const draggedIndex = sortedBlocks.findIndex(b => {
      const bId = typeof b.blockId === 'string' ? parseInt(b.blockId, 10) : b.blockId;
      const dragId = typeof reorderDragBlockId === 'string' ? parseInt(reorderDragBlockId, 10) : reorderDragBlockId;
      return bId === dragId;
    });
    
    if (draggedIndex === -1 || pageIndex === null) {
      setIsReorderingBlock(false);
      setReorderDragBlockId(null);
      setReorderDragIsLeft(true);
      setReorderDragStartY(null);
      setReorderDragOffset(0);
      reorderDragOffsetRef.current = 0;
      return;
    }

    // Get actual current mouse Y position
    const currentY = e.clientY;
    
    // Find target index based on actual mouse position
    // Only consider blocks from the current page (left or right)
    const dragId = typeof reorderDragBlockId === 'string' ? parseInt(reorderDragBlockId, 10) : reorderDragBlockId;
    const blockItems = Array.from(blockItemRefs.current.entries())
      .filter(([key]) => {
        // Key format is `${pageIndex}-${blockId}`
        // Handle the case where blockId might contain dashes by splitting only on first dash
        const dashIndex = key.indexOf('-');
        if (dashIndex === -1) {
          // Old format key (just blockId) - skip it
          return false;
        }
        const keyPageIndex = Number(key.substring(0, dashIndex));
        const keyBlockId = Number(key.substring(dashIndex + 1));
        
        // Only include blocks from the current page
        if (isNaN(keyPageIndex) || keyPageIndex !== pageIndex) {
          return false;
        }
        
        // Only include blocks that are in the current page's sorted blocks list
        const isInCurrentPage = sortedBlocks.some(b => {
          const blockIdNum = typeof b.blockId === 'string' ? parseInt(b.blockId, 10) : b.blockId;
          return blockIdNum === keyBlockId;
        });
        return isInCurrentPage && keyBlockId !== dragId;
      })
      .map(([key, element]) => {
        // Parse key: format is `${pageIndex}-${blockId}`
        const dashIndex = key.indexOf('-');
        const keyBlockId = Number(key.substring(dashIndex + 1));
        const rect = element.getBoundingClientRect();
        const blockIndex = sortedBlocks.findIndex(b => {
          const bId = typeof b.blockId === 'string' ? parseInt(b.blockId, 10) : b.blockId;
          return bId === keyBlockId;
        });
        return {
          id: keyBlockId,
          blockIndex,
          top: rect.top,
          bottom: rect.bottom,
          center: rect.top + rect.height / 2,
          distance: Math.abs(currentY - (rect.top + rect.height / 2))
        };
      })
      .sort((a, b) => a.blockIndex - b.blockIndex);

    let targetIndex = draggedIndex;
    
    if (blockItems.length === 0) {
      targetIndex = draggedIndex;
    } else {
      const closestItem = blockItems.reduce((closest, item) => 
        item.distance < closest.distance ? item : closest
      );
      
      const itemUnderMouse = blockItems.find(item => 
        currentY >= item.top && currentY <= item.bottom
      );
      
      if (itemUnderMouse) {
        if (currentY < itemUnderMouse.center) {
          targetIndex = itemUnderMouse.blockIndex;
        } else {
          targetIndex = itemUnderMouse.blockIndex + 1;
        }
      } else {
        if (currentY < closestItem.top) {
          targetIndex = closestItem.blockIndex;
        } else if (currentY > closestItem.bottom) {
          targetIndex = closestItem.blockIndex + 1;
        } else {
          if (currentY < closestItem.center) {
            targetIndex = closestItem.blockIndex;
          } else {
            targetIndex = closestItem.blockIndex + 1;
          }
        }
      }
      
      targetIndex = Math.max(0, Math.min(targetIndex, sortedBlocks.length));
    }

    // Adjust target index to account for the item being removed
    let adjustedTargetIndex = targetIndex;
    if (targetIndex > draggedIndex) {
      adjustedTargetIndex = targetIndex - 1;
    }

    // Update blockReadOrder values instead of reordering the array
    if (adjustedTargetIndex !== draggedIndex && adjustedTargetIndex >= 0 && adjustedTargetIndex <= sortedBlocks.length) {
      // Get all blocks (not sorted) to update - ONLY from the current page
      const allBlocks = wordDataState.blocksByPage.get(pageIndex) || [];
      
      // Defensive check: ensure we're only working with blocks from the correct page
      if (allBlocks.length === 0) {
        console.warn(`[BlockReorder] No blocks found for pageIndex ${pageIndex}`);
        setIsReorderingBlock(false);
        setReorderDragBlockId(null);
        setReorderDragIsLeft(true);
        setReorderDragStartY(null);
        setReorderDragOffset(0);
        reorderDragOffsetRef.current = 0;
        return;
      }
      
      const draggedBlock = sortedBlocks[draggedIndex];
      const draggedBlockIdNum = typeof draggedBlock.blockId === 'string' ? parseInt(draggedBlock.blockId, 10) : draggedBlock.blockId;
      const draggedReadOrder = draggedBlock.blockReadOrder || (draggedIndex + 1);
      const newReadOrder = adjustedTargetIndex + 1;
      
      // Create a new array with updated blockReadOrder values
      // IMPORTANT: Only update blocks from the current page (allBlocks already filtered by pageIndex)
      const updatedBlocks = allBlocks.map(block => {
        const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
        const currentReadOrder = block.blockReadOrder || 0;
        
        if (blockIdNum === draggedBlockIdNum) {
          // This is the dragged block - set its new blockReadOrder
          return {
            ...block,
            blockReadOrder: newReadOrder
          };
        } else {
          // Update other blocks' blockReadOrder based on the new position
          if (draggedIndex < adjustedTargetIndex) {
            // Moving down: shift blocks between draggedIndex+1 and adjustedTargetIndex up (decrease by 1)
            if (currentReadOrder > draggedReadOrder && currentReadOrder <= newReadOrder) {
              return {
                ...block,
                blockReadOrder: currentReadOrder - 1
              };
            }
          } else {
            // Moving up: shift blocks between adjustedTargetIndex and draggedIndex-1 down (increase by 1)
            if (currentReadOrder >= newReadOrder && currentReadOrder < draggedReadOrder) {
              return {
                ...block,
                blockReadOrder: currentReadOrder + 1
              };
            }
          }
          
          return block;
        }
      });

      // Update blocks in context - ONLY for the current page
      console.log(`[BlockReorder] Updating blocks for pageIndex ${pageIndex}, draggedBlockId: ${draggedBlockIdNum}, newReadOrder: ${newReadOrder}`);
      wordDataActions.updatePageBlocks(pageIndex, updatedBlocks);
    }

    setIsReorderingBlock(false);
    setReorderDragBlockId(null);
    setReorderDragIsLeft(true);
    setReorderDragStartY(null);
    setReorderDragOffset(0);
    reorderDragOffsetRef.current = 0;
  }, [isReorderingBlock, reorderDragBlockId, reorderDragIsLeft, reorderDragStartY, activeSpread.leftPageIndex, activeSpread.rightPageIndex, wordDataState.blocksByPage, wordDataActions]);

  // Clean up old-format blockItemRefs entries (keys without pageIndex prefix)
  useEffect(() => {
    // Remove any entries that don't have the composite key format (pageIndex-blockId)
    const keysToRemove: string[] = [];
    blockItemRefs.current.forEach((_, key) => {
      if (typeof key === 'string' && key.indexOf('-') === -1) {
        // Old format key (just blockId) - mark for removal
        keysToRemove.push(key);
      }
    });
    keysToRemove.forEach(key => blockItemRefs.current.delete(key));
  }, [activeSpread.leftPageIndex, activeSpread.rightPageIndex]);

  // Set up mouse event listeners for block reordering
  useEffect(() => {
    if (isReorderingBlock) {
      const handleMouseMove = (e: MouseEvent) => handleBlockReorderMouseMove(e);
      const handleMouseUp = (e: MouseEvent) => handleBlockReorderMouseUp(e);
      
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isReorderingBlock, handleBlockReorderMouseMove, handleBlockReorderMouseUp]);

  // Sync activeSpread to WordDataContext when it changes
  useEffect(() => {
    wordDataActions.setActiveSpread(activeSpread.leftPageIndex, activeSpread.rightPageIndex);
  }, [activeSpread.leftPageIndex, activeSpread.rightPageIndex, wordDataActions]);

  // Clear merge selection when page changes
  useEffect(() => {
    setMergeSourceBlockId(null);
    setMergeSourcePageIndex(null);
  }, [activeSpread.leftPageIndex, activeSpread.rightPageIndex]);

  // Cancel merge function - returns true if merge was active
  const cancelMerge = useCallback(() => {
    const wasActive = mergeSourceBlockId !== null;
    setMergeSourceBlockId(null);
    setMergeSourcePageIndex(null);
    return wasActive;
  }, [mergeSourceBlockId]);
  
  // Get blocks from WordDataContext and sort by blockReadOrder
  const leftBlocks = activeSpread.leftPageIndex !== null 
    ? [...(wordDataState.blocksByPage.get(activeSpread.leftPageIndex) || [])]
        .sort((a, b) => (a.blockReadOrder || 0) - (b.blockReadOrder || 0))
    : [];
  const rightBlocks = activeSpread.rightPageIndex !== null
    ? [...(wordDataState.blocksByPage.get(activeSpread.rightPageIndex) || [])]
        .sort((a, b) => (a.blockReadOrder || 0) - (b.blockReadOrder || 0))
    : [];

  // Handle block merge
  const handleBlockMerge = useCallback((blockId: number | string, pageIndex: number | null, isLeft: boolean) => {
    if (pageIndex === null) return;

    // If no block is selected for merging, select this one
    if (mergeSourceBlockId === null) {
      setMergeSourceBlockId(blockId);
      setMergeSourcePageIndex(pageIndex);
      return;
    }

    // If clicking the same block, cancel merge
    if (mergeSourceBlockId === blockId && mergeSourcePageIndex === pageIndex) {
      setMergeSourceBlockId(null);
      setMergeSourcePageIndex(null);
      return;
    }

    // If clicking a different block on a different page, cancel and select new one
    if (mergeSourcePageIndex !== pageIndex) {
      setMergeSourceBlockId(blockId);
      setMergeSourcePageIndex(pageIndex);
      return;
    }

    // Perform merge - both blocks are on the same page
    const currentBlocks = wordDataState.blocksByPage.get(pageIndex) || [];
    const sourceBlock = currentBlocks.find(b => {
      const bId = typeof b.blockId === 'string' ? parseInt(b.blockId, 10) : b.blockId;
      const sourceId = typeof mergeSourceBlockId === 'string' ? parseInt(mergeSourceBlockId, 10) : mergeSourceBlockId;
      return bId === sourceId;
    });
    const targetBlock = currentBlocks.find(b => {
      const bId = typeof b.blockId === 'string' ? parseInt(b.blockId, 10) : b.blockId;
      const targetId = typeof blockId === 'string' ? parseInt(blockId, 10) : blockId;
      return bId === targetId;
    });

    if (!sourceBlock || !targetBlock) {
      setMergeSourceBlockId(null);
      setMergeSourcePageIndex(null);
      return;
    }

    // Determine which block to merge into (lower blockReadOrder) and which to remove
    const sourceReadOrder = sourceBlock.blockReadOrder || 0;
    const targetReadOrder = targetBlock.blockReadOrder || 0;
    
    const blockToMergeInto = sourceReadOrder < targetReadOrder ? sourceBlock : targetBlock;
    const blockToRemove = sourceReadOrder < targetReadOrder ? targetBlock : sourceBlock;

    // Merge words: add words from block-to-remove to block-to-merge-into after existing words
    const mergedWords = [
      ...blockToMergeInto.words,
      ...blockToRemove.words
    ];

    // Create merged block
    const mergedBlock: BlockData = {
      ...blockToMergeInto,
      words: mergedWords,
      // Update bounding box to encompass both blocks
      boundingBox: {
        x: Math.min(blockToMergeInto.boundingBox.x, blockToRemove.boundingBox.x),
        y: Math.min(blockToMergeInto.boundingBox.y, blockToRemove.boundingBox.y),
        width: Math.max(
          blockToMergeInto.boundingBox.x + blockToMergeInto.boundingBox.width,
          blockToRemove.boundingBox.x + blockToRemove.boundingBox.width
        ) - Math.min(blockToMergeInto.boundingBox.x, blockToRemove.boundingBox.x),
        height: Math.max(
          blockToMergeInto.boundingBox.y + blockToMergeInto.boundingBox.height,
          blockToRemove.boundingBox.y + blockToRemove.boundingBox.height
        ) - Math.min(blockToMergeInto.boundingBox.y, blockToRemove.boundingBox.y),
      },
      // Track merged blocks
      mergedFromBlocks: [
        ...(Array.isArray((blockToMergeInto as any).mergedFromBlocks) ? (blockToMergeInto as any).mergedFromBlocks : []),
        ...(Array.isArray((blockToRemove as any).mergedFromBlocks) ? (blockToRemove as any).mergedFromBlocks : []),
        typeof blockToRemove.blockId === 'string' ? parseInt(blockToRemove.blockId, 10) : blockToRemove.blockId
      ]
    };

    // Update blocks: replace block-to-merge-into with merged block, remove block-to-remove
    const updatedBlocks = currentBlocks
      .map(block => {
        const bId = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
        const mergeIntoId = typeof blockToMergeInto.blockId === 'string' ? parseInt(blockToMergeInto.blockId, 10) : blockToMergeInto.blockId;
        if (bId === mergeIntoId) {
          return mergedBlock;
        }
        return block;
      })
      .filter(block => {
        const bId = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
        const removeId = typeof blockToRemove.blockId === 'string' ? parseInt(blockToRemove.blockId, 10) : blockToRemove.blockId;
        return bId !== removeId;
      });

    // Update blocks in context
    wordDataActions.updatePageBlocks(pageIndex, updatedBlocks);

    // Clear merge selection
    setMergeSourceBlockId(null);
    setMergeSourcePageIndex(null);
  }, [mergeSourceBlockId, mergeSourcePageIndex, wordDataState.blocksByPage, wordDataActions]);
  
  // Get word data from WordDataContext
  const leftWordData = activeSpread.leftPageIndex !== null
    ? wordDataState.wordDataByPage.get(activeSpread.leftPageIndex)
    : null;
  const rightWordData = activeSpread.rightPageIndex !== null
    ? wordDataState.wordDataByPage.get(activeSpread.rightPageIndex)
    : null;
  
  // Log image_dimensions for debugging
  useEffect(() => {
    if (leftWordData) {
      console.log(`[SPREAD EDITOR] Left page (index ${activeSpread.leftPageIndex}) image_dimensions:`, leftWordData.image_dimensions);
    }
    if (rightWordData) {
      console.log(`[SPREAD EDITOR] Right page (index ${activeSpread.rightPageIndex}) image_dimensions:`, rightWordData.image_dimensions);
    }
  }, [leftWordData, rightWordData, activeSpread.leftPageIndex, activeSpread.rightPageIndex]);
  
  // Store original blocks for change detection
  const [leftOriginalBlocks, setLeftOriginalBlocks] = useState<BlockData[]>([]);
  const [rightOriginalBlocks, setRightOriginalBlocks] = useState<BlockData[]>([]);
  
  // Store original pageType values for change detection
  const [leftOriginalPageType, setLeftOriginalPageType] = useState<PageType | undefined>(undefined);
  const [rightOriginalPageType, setRightOriginalPageType] = useState<PageType | undefined>(undefined);

  // Function to check if a page has changes (blocks OR pageType)
  const checkPageHasChanges = useCallback((
    currentBlocks: BlockData[],
    originalBlocks: BlockData[],
    currentPageType: PageType | undefined,
    originalPageType: PageType | undefined
  ): boolean => {
    // Check if blocks changed
    // If both are empty, no changes
    if (originalBlocks.length === 0 && currentBlocks.length === 0) {
      return false;
    }
    
    // If lengths differ, there's definitely a change (blocks added or removed)
    if (originalBlocks.length !== currentBlocks.length) {
      return true;
    }
    
    // Sort both arrays by blockReadOrder for comparison to ensure consistent ordering
    const sortedCurrent = [...currentBlocks].sort((a, b) => (a.blockReadOrder || 0) - (b.blockReadOrder || 0));
    const sortedOriginal = [...originalBlocks].sort((a, b) => (a.blockReadOrder || 0) - (b.blockReadOrder || 0));
    
    // Create maps by blockId for easier comparison
    const currentMap = new Map(sortedCurrent.map(b => {
      const blockId = typeof b.blockId === 'string' ? parseInt(b.blockId, 10) : b.blockId;
      return [blockId, b];
    }));
    const originalMap = new Map(sortedOriginal.map(b => {
      const blockId = typeof b.blockId === 'string' ? parseInt(b.blockId, 10) : b.blockId;
      return [blockId, b];
    }));
    
    // Check if any block's blockReadOrder changed
    for (const [blockId, currentBlock] of currentMap.entries()) {
      const originalBlock = originalMap.get(blockId);
      if (!originalBlock) {
        return true; // Block was added or removed
      }
      if ((currentBlock.blockReadOrder || 0) !== (originalBlock.blockReadOrder || 0)) {
        return true; // blockReadOrder changed
      }
    }
    
    // Also check if any blocks were removed
    for (const [blockId] of originalMap.entries()) {
      if (!currentMap.has(blockId)) {
        return true; // Block was removed
      }
    }
    
    // Fallback to JSON comparison for other block property changes
    const blocksChanged = JSON.stringify(sortedCurrent) !== JSON.stringify(sortedOriginal);
    
    // Check if pageType changed
    const pageTypeChanged = currentPageType !== originalPageType;
    
    return blocksChanged || pageTypeChanged;
  }, []);

  // Update original blocks and pageType when blocks are first loaded
  // Use a ref to track if we've already initialized to prevent resetting after changes
  const leftBlocksInitializedRef = useRef(false);
  const rightBlocksInitializedRef = useRef(false);
  
  useEffect(() => {
    // Only set original blocks once when blocks are first loaded for this page
    if (leftBlocks.length > 0 && leftOriginalBlocks.length === 0 && !leftBlocksInitializedRef.current) {
      setLeftOriginalBlocks(JSON.parse(JSON.stringify(leftBlocks)));
      if (leftPageDataFromProps?.pageType !== undefined) {
        setLeftOriginalPageType(leftPageDataFromProps.pageType as PageType);
      }
      leftBlocksInitializedRef.current = true;
    }
  }, [leftBlocks, leftOriginalBlocks.length, leftPageDataFromProps?.pageType]);
  
  useEffect(() => {
    // Only set original blocks once when blocks are first loaded for this page
    if (rightBlocks.length > 0 && rightOriginalBlocks.length === 0 && !rightBlocksInitializedRef.current) {
      setRightOriginalBlocks(JSON.parse(JSON.stringify(rightBlocks)));
      if (rightPageDataFromProps?.pageType !== undefined) {
        setRightOriginalPageType(rightPageDataFromProps.pageType as PageType);
      }
      rightBlocksInitializedRef.current = true;
    }
  }, [rightBlocks, rightOriginalBlocks.length, rightPageDataFromProps?.pageType]);

  // Reset original blocks and pageType when spread changes
  useEffect(() => {
    setLeftOriginalBlocks([]);
    setRightOriginalBlocks([]);
    setLeftOriginalPageType(undefined);
    setRightOriginalPageType(undefined);
    // Reset the initialization flags when spread changes
    leftBlocksInitializedRef.current = false;
    rightBlocksInitializedRef.current = false;
  }, [activeSpread.leftPageIndex, activeSpread.rightPageIndex]);

  // Keyboard shortcut refs - declare before change detection so they're available
  const activeSpreadRef = useRef(activeSpread);
  const hasBookChangesRef = useRef(hasBookChanges);
  const isSavingRef = useRef(isSaving);
  
  // Detect changes by comparing current state with original state (blocks AND pageType)
  useEffect(() => {
    // Only skip comparison if original blocks haven't been initialized yet
    // If original blocks exist but current blocks are empty, that's a valid change (all blocks deleted)
    if (leftOriginalBlocks.length === 0) {
      // Not ready to compare yet - original blocks not initialized
      return;
    }

    const hasChanges = checkPageHasChanges(
      leftBlocks,
      leftOriginalBlocks,
      (leftPageDataFromProps?.pageType || '') as PageType,
      leftOriginalPageType
    );
    
    setActiveSpread(prev => {
      if (hasChanges !== prev.leftHasChanges) {
        const newSpread = {
          ...prev,
          leftHasChanges: hasChanges,
        };
        // Update ref immediately so keyboard handler sees the change
        activeSpreadRef.current = newSpread;
        return newSpread;
      }
      return prev;
    });
  }, [leftBlocks, leftOriginalBlocks, leftPageDataFromProps?.pageType, leftOriginalPageType, checkPageHasChanges]);

  useEffect(() => {
    // Only skip comparison if original blocks haven't been initialized yet
    // If original blocks exist but current blocks are empty, that's a valid change (all blocks deleted)
    if (rightOriginalBlocks.length === 0) {
      // Not ready to compare yet - original blocks not initialized
      return;
    }

    const hasChanges = checkPageHasChanges(
      rightBlocks,
      rightOriginalBlocks,
      (rightPageDataFromProps?.pageType || '') as PageType,
      rightOriginalPageType
    );
    
    setActiveSpread(prev => {
      if (hasChanges !== prev.rightHasChanges) {
        const newSpread = {
          ...prev,
          rightHasChanges: hasChanges,
        };
        // Update ref immediately so keyboard handler sees the change
        activeSpreadRef.current = newSpread;
        return newSpread;
      }
      return prev;
    });
  }, [rightBlocks, rightOriginalBlocks, rightPageDataFromProps?.pageType, rightOriginalPageType, checkPageHasChanges]);

  // Handle block click (for editing)
  const [editingLeftBlock, setEditingLeftBlock] = useState<BlockData | null>(null);
  const [editingRightBlock, setEditingRightBlock] = useState<BlockData | null>(null);

  const handleBlockEdit = useCallback((block: BlockData, isLeft: boolean) => {
    if (isLeft) {
      setEditingLeftBlock(block);
      setEditingRightBlock(null); // Close right editor if open
    } else {
      setEditingRightBlock(block);
      setEditingLeftBlock(null); // Close left editor if open
    }
  }, []);

  const handleBlockCancel = useCallback((isLeft: boolean) => {
    if (isLeft) {
      setEditingLeftBlock(null);
    } else {
      setEditingRightBlock(null);
    }
    wordDataActions.setHighlightedBlock(null);
  }, [wordDataActions]);

  const closeAllBlockEditors = useCallback(() => {
    setEditingLeftBlock(null);
    setEditingRightBlock(null);
    wordDataActions.setHighlightedBlock(null);
  }, [wordDataActions]);

  const handleHighlightBlock = useCallback((blockId: number, boundingBox: { x: number; y: number; width: number; height: number }) => {
    wordDataActions.setHighlightedBlock({ blockId, boundingBox });
  }, [wordDataActions]);

  const handleClearHighlight = useCallback(() => {
    wordDataActions.setHighlightedBlock(null);
  }, [wordDataActions]);

  const handleBlockUpdateFromEditor = useCallback((updatedBlock: BlockData, isLeft: boolean) => {
    if (isLeft) {
      handleLeftBlockUpdate(updatedBlock);
      setEditingLeftBlock(updatedBlock); // Update the editing block state
    } else {
      handleRightBlockUpdate(updatedBlock);
      setEditingRightBlock(updatedBlock); // Update the editing block state
    }
  }, [handleLeftBlockUpdate, handleRightBlockUpdate]);

  // Handler to remove block reads for all blocks on a page
  const handleRemoveBlockReads = useCallback((pageIndex: number | null, isLeft: boolean) => {
    if (pageIndex === null) return;
    
    // Get fresh blocks from context to avoid stale closure
    const currentBlocks = isLeft 
      ? (wordDataState.blocksByPage.get(pageIndex) || [])
      : (wordDataState.blocksByPage.get(pageIndex) || []);
    
    if (currentBlocks.length === 0) return;
    
    // Update all blocks to set readBlock to false
    const updatedBlocks = currentBlocks.map(block => ({
      ...block,
      readBlock: false
    }));
    
    // Update all blocks at once
    // Change detection will happen automatically via the useEffect that calls checkPageHasChanges
    wordDataActions.updatePageBlocks(pageIndex, updatedBlocks);
  }, [wordDataState.blocksByPage, wordDataActions]);

  // Handler to add a new block with a new word
  const handleAddBlock = useCallback((pageIndex: number | null, isLeft: boolean) => {
    if (pageIndex === null) return;
    
    // Get fresh blocks from context
    const currentBlocks = wordDataState.blocksByPage.get(pageIndex) || [];
    
    // Get all existing block IDs to generate a new one
    const allBlockIds = currentBlocks.map(block => {
      const blockId = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
      return blockId;
    });
    const newBlockId = getNextAvailableId(allBlockIds);
    
    // Get all existing word IDs to generate a new one
    const allWordIds: (string | number)[] = [];
    currentBlocks.forEach(block => {
      block.words.forEach(word => {
        allWordIds.push(word.wordId);
      });
    });
    const newWordId = getNextAvailableId(allWordIds);
    
    // Create new word at position 100, 100 with width 150, height 50
    const newWord: any = {
      wordId: newWordId,
      text: '',
      boundingBox: {
        x: 100,
        y: 100,
        width: 150,
        height: 50
      },
      confidence: 100
    };
    
    // Create new block with the new word
    const newBlock: BlockData = {
      blockId: newBlockId,
      blockType: '',
      boundingBox: {
        x: 100,
        y: 100,
        width: 150,
        height: 50
      },
      words: [newWord],
      readBlock: true,
      blockReadOrder: currentBlocks.length + 1,
      'ai-confidence': 0,
      possibleIssue: '-'
    };
    
    // Add the new block to the page
    const updatedBlocks = [...currentBlocks, newBlock];
    wordDataActions.updatePageBlocks(pageIndex, updatedBlocks);
    
    // Enable text edit mode and select the word
    wordDataActions.setEditMode('text');
    wordDataActions.setSelectedWord(String(newWordId), pageIndex);
    
    // Set the editing block so it opens in the editor
    if (isLeft) {
      setEditingLeftBlock(newBlock);
    } else {
      setEditingRightBlock(newBlock);
    }
  }, [wordDataState.blocksByPage, wordDataActions]);

  // Sync editing block state when blocks update (e.g., from external changes)
  useEffect(() => {
    if (editingLeftBlock) {
      const updatedBlock = leftBlocks.find(b => b.blockId === editingLeftBlock.blockId);
      if (updatedBlock) {
        setEditingLeftBlock(updatedBlock);
      }
    }
  }, [leftBlocks, editingLeftBlock]);

  useEffect(() => {
    if (editingRightBlock) {
      const updatedBlock = rightBlocks.find(b => b.blockId === editingRightBlock.blockId);
      if (updatedBlock) {
        setEditingRightBlock(updatedBlock);
      }
    }
  }, [rightBlocks, editingRightBlock]);

  // Close editors when spread changes
  useEffect(() => {
    setEditingLeftBlock(null);
    setEditingRightBlock(null);
    wordDataActions.setHighlightedBlock(null);
  }, [currentLeftPage, currentRightPage, wordDataActions]);

  // Helper function to get word data filename for a page
  const getWordDataStrategy = useCallback((page: PageData | null): string | null => {
    if (!page) return null;
    
    // Only load words file if text_extracted is "enhanced" for this page
    if (page.text_extracted !== 'enhanced' && page.text_extracted !== true) {
      return null;
    }
    
    // Skip pages that shouldn't have word data (based on page type)
    const skipWordDataTypes = ['blank-page', 'no-text'];
    if (page.pageType && skipWordDataTypes.includes(page.pageType)) {
      return null;
    }
    
    // Use spine.json data - only use words field
    if (page.words) {
      return page.words;
    }
    
    return null;
  }, []);

  // Convert blocks back to original JSON format for saving
  const convertBlocksToWordData = useCallback((originalData: any, blocks: BlockData[]): EnhancedPageWordsData | any => {
    try {
      if (!originalData || !Array.isArray(blocks)) {
        throw new Error('Invalid input data for conversion');
      }

      const updatedData = { ...originalData };
      
      if (updatedData.blocks && Array.isArray(updatedData.blocks) && updatedData.total_blocks !== undefined) {
        if (updatedData.total_words === undefined || updatedData.total_words === null || !updatedData.page) {
          throw new Error('Invalid enhanced JSON structure');
        }

        const originalBlocksMap = new Map(
          (updatedData.blocks as any[]).map((block: any) => [block.blockId, block])
        );

        updatedData.blocks = blocks.map(block => {
          // blockType can be empty string (for "No Block Type"), so check for undefined/null instead
          if (!block.blockId || block.blockType === undefined || block.blockType === null || !block.boundingBox || !Array.isArray(block.words)) {
            throw new Error(`Invalid block data for block ${block.blockId}`);
          }

          const originalBlock = originalBlocksMap.get(block.blockId);
          const sourceOriginalBlock = (block as any).originalBlockData || originalBlock;
          
          const preservedProperties: any = {};
          if (sourceOriginalBlock) {
            preservedProperties.modifiedWords = (block as any).modifiedWords !== undefined ? (block as any).modifiedWords : (sourceOriginalBlock as any).modifiedWords;
            preservedProperties.mergedFromBlocks = (block as any).mergedFromBlocks !== undefined 
              ? (block as any).mergedFromBlocks 
              : (sourceOriginalBlock as any).mergedFromBlocks;
            preservedProperties.review = (block as any).review !== undefined ? (block as any).review : (sourceOriginalBlock as any).review;
            
            if (sourceOriginalBlock.possibleIssue !== undefined) {
              const currentIssue = block.possibleIssue;
              if (currentIssue === undefined || currentIssue === '-' || currentIssue === 'none') {
                if (sourceOriginalBlock.possibleIssue !== '-' && sourceOriginalBlock.possibleIssue !== 'none') {
                  preservedProperties.possibleIssue = sourceOriginalBlock.possibleIssue;
                } else {
                  preservedProperties.possibleIssue = currentIssue || sourceOriginalBlock.possibleIssue;
                }
              } else {
                preservedProperties.possibleIssue = currentIssue;
              }
            }
            
            if (sourceOriginalBlock['ai-confidence'] !== undefined) {
              const currentConfidence = block['ai-confidence'];
              if (currentConfidence === undefined || currentConfidence === 0) {
                if (sourceOriginalBlock['ai-confidence'] !== 0) {
                  preservedProperties['ai-confidence'] = sourceOriginalBlock['ai-confidence'];
                } else {
                  preservedProperties['ai-confidence'] = currentConfidence ?? sourceOriginalBlock['ai-confidence'];
                }
              } else {
                preservedProperties['ai-confidence'] = currentConfidence;
              }
            }
          }
          
          const finalBlock = {
            blockId: (block as any).originalBlockId || block.blockId, // Use original ID if adjusted
            blockType: block.blockType,
            readBlock: block.readBlock !== undefined ? Boolean(block.readBlock) : (sourceOriginalBlock?.readBlock ?? false),
            words: (() => {
              // Collect all words first to determine next available IDs
              const allWords = block.words.map(word => ({
                word,
                originalWordId: (word as any).originalWordId || word.wordId,
                occurrenceIndex: (word as any).originalWordOccurrenceIndex ?? 0
              }));
              
              // Get all existing word IDs from original data to find next available
              const allOriginalWordIds: (string | number)[] = [];
              (updatedData.blocks as any[]).forEach((origBlock: any) => {
                if (origBlock.words && Array.isArray(origBlock.words)) {
                  origBlock.words.forEach((w: any) => allOriginalWordIds.push(w.wordId));
                }
              });
              
              // Assign new sequential IDs
              let nextId = getNextAvailableId(allOriginalWordIds);
              const usedIds = new Set(allOriginalWordIds);
              
              return allWords.map(({ word, originalWordId, occurrenceIndex }) => {
                // Use current boundingBox if word has been edited (different from normalized originalBoundingBox),
                // otherwise use originalBoundingBox to preserve original file values
                const originalBoundingBox = (word as any).originalBoundingBox;
                let boundingBoxToSave = word.boundingBox;
                
                if (originalBoundingBox) {
                  // Calculate what the normalized bounding box would be
                  const normalizedOriginal = { ...originalBoundingBox };
                  if (normalizedOriginal.width === 0) {
                    normalizedOriginal.width = 150;
                  }
                  if (normalizedOriginal.height === 0) {
                    normalizedOriginal.height = 50;
                  }
                  
                  // If current boundingBox equals normalized original, word hasn't been edited - use original
                  // Otherwise, word has been edited - use current boundingBox
                  if (normalizedOriginal.x === word.boundingBox.x &&
                      normalizedOriginal.y === word.boundingBox.y &&
                      normalizedOriginal.width === word.boundingBox.width &&
                      normalizedOriginal.height === word.boundingBox.height) {
                    boundingBoxToSave = originalBoundingBox;
                  }
                }
                
                // If word has adjusted ID, find the original word by occurrence index and assign new ID
                // Otherwise, use original ID or assign next available
                let wordIdToSave: string | number;
                let foundOriginalId: string | number | null = null;
                
                if (isAdjustedId(word.wordId)) {
                  // This word had a collision - assign new sequential ID
                  // Try to find the original word in the original data by occurrence index
                  const originalIdInfo = extractOriginalIdInfo(word.wordId, [originalWordId]);
                  if (originalIdInfo) {
                    // Find the nth occurrence in the original block's words
                    const originalBlock = sourceOriginalBlock;
                    if (originalBlock && originalBlock.words && Array.isArray(originalBlock.words)) {
                      const originalWord = findNthOccurrence(
                        originalBlock.words,
                        (w: any) => w.wordId,
                        originalIdInfo.originalId,
                        originalIdInfo.occurrenceIndex
                      );
                      if (originalWord) {
                        foundOriginalId = originalWord.wordId;
                      }
                    }
                  }
                  
                  while (usedIds.has(nextId)) {
                    nextId++;
                  }
                  wordIdToSave = nextId;
                  usedIds.add(nextId);
                  nextId++;
                  
                  console.log(`[ID COLLISION - SAVE] Word ID reassigned: adjusted=${word.wordId}, new=${wordIdToSave}, original=${foundOriginalId || originalWordId || 'not found'}`);
                } else {
                  // No collision, use original ID
                  wordIdToSave = originalWordId;
                  if (!usedIds.has(wordIdToSave)) {
                    usedIds.add(wordIdToSave);
                    if (typeof wordIdToSave === 'number' && wordIdToSave >= nextId) {
                      nextId = wordIdToSave + 1;
                    }
                  }
                }
                
                return {
                  wordId: wordIdToSave,
                  text: word.text,
                  boundingBox: {
                    x: Math.round(Number(boundingBoxToSave.x) * 10) / 10,
                    y: Math.round(Number(boundingBoxToSave.y) * 10) / 10,
                    width: Math.round(Number(boundingBoxToSave.width) * 10) / 10,
                    height: Math.round(Number(boundingBoxToSave.height) * 10) / 10
                  },
                  confidence: Number(word.confidence)
                };
              });
            })(),
            boundingBox: {
              x: Math.round(Number(block.boundingBox.x) * 10) / 10,
              y: Math.round(Number(block.boundingBox.y) * 10) / 10,
              width: Math.round(Number(block.boundingBox.width) * 10) / 10,
              height: Math.round(Number(block.boundingBox.height) * 10) / 10
            },
            blockReadOrder: block.blockReadOrder !== undefined ? Number(block.blockReadOrder) : (sourceOriginalBlock?.blockReadOrder ?? 0),
            modifiedWords: (block as any).modifiedWords !== undefined ? (block as any).modifiedWords : (preservedProperties.modifiedWords ?? null),
            mergedFromBlocks: (block as any).mergedFromBlocks !== undefined 
              ? (Array.isArray((block as any).mergedFromBlocks) ? (block as any).mergedFromBlocks : null)
              : (preservedProperties.mergedFromBlocks ?? null),
            review: (block as any).review !== undefined ? (block as any).review : (preservedProperties.review ?? 'needs-review'),
            possibleIssue: preservedProperties.possibleIssue !== undefined ? preservedProperties.possibleIssue : (block.possibleIssue ?? 'none'),
            'ai-confidence': preservedProperties['ai-confidence'] !== undefined ? Number(preservedProperties['ai-confidence']) : (block['ai-confidence'] !== undefined ? Number(block['ai-confidence']) : 0)
          };
          
          return finalBlock;
        });

        updatedData.total_blocks = blocks.length;
        updatedData.total_words = blocks.reduce((total, block) => total + (block.words?.length || 0), 0);

        return updatedData as EnhancedPageWordsData;
      }
      
      return updatedData;
    } catch (error) {
      console.error('[SPREAD EDITOR] Error converting blocks to word data:', error);
      throw new Error(`Failed to convert blocks to word data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, []);

  // Save handler for both pages
  const handleSave = useCallback(async () => {
    if (!activeSpread.leftHasChanges && !activeSpread.rightHasChanges && !hasBookChanges) {
      return;
    }

    setIsSaving(true);
    
    // Track changes separately for book-level, left page, and right page
    const bookChanges: string[] = [];
    const leftPageChanges: string[] = [];
    const rightPageChanges: string[] = [];

    try {
      // First, update spine.json if any pageType changed or notes changed (both pages share the same spine.json)
      let updatedSpineData = spineData ? { ...spineData } : null;
      let spineNeedsSave = false;
      
      if (updatedSpineData && updatedSpineData.pages) {
        const pagesArray = updatedSpineData.pages;
        
        // Update left page in spine if it has changes
        if (activeSpread.leftHasChanges && activeSpread.leftPageIndex !== null) {
          const leftPageFromProps = pages.find(p => p['page-index'] === activeSpread.leftPageIndex);
          if (leftPageFromProps) {
            const pageIndexInArray = pagesArray.findIndex((p: PageData) => p['page-index'] === activeSpread.leftPageIndex);
            if (pageIndexInArray !== -1 && leftPageFromProps.pageType !== undefined) {
              pagesArray[pageIndexInArray] = { 
                ...pagesArray[pageIndexInArray], 
                pageType: leftPageFromProps.pageType 
              };
              spineNeedsSave = true;
            }
          }
        }
        
        // Update right page in spine if it has changes
        if (activeSpread.rightHasChanges && activeSpread.rightPageIndex !== null) {
          const rightPageFromProps = pages.find(p => p['page-index'] === activeSpread.rightPageIndex);
          if (rightPageFromProps) {
            const pageIndexInArray = pagesArray.findIndex((p: PageData) => p['page-index'] === activeSpread.rightPageIndex);
            if (pageIndexInArray !== -1 && rightPageFromProps.pageType !== undefined) {
              pagesArray[pageIndexInArray] = { 
                ...pagesArray[pageIndexInArray], 
                pageType: rightPageFromProps.pageType 
              };
              spineNeedsSave = true;
            }
          }
        }
        
        // Update approval (including notes) in spine if notes changed
        // Note: Notes are saved to both all-books (via onApprovalChange) and spine
        if (hasBookChanges && updatedSpineData) {
          if (!updatedSpineData.approval) {
            updatedSpineData.approval = {};
          }
          updatedSpineData.approval.notes = currentApproval?.notes || '';
          spineNeedsSave = true;
          bookChanges.push('notes updated');
        }
        
        // Update TTS metadata in spine if available and different
        if (ttsChars !== null && updatedSpineData) {
          if (!updatedSpineData.book) {
            updatedSpineData.book = {};
          }
          const currentTtsChars = updatedSpineData.book.tts_chars;
          const currentTtsCostAzure = updatedSpineData.book.tts_cost_azure;
          const currentTtsCostElevenlabs = updatedSpineData.book.tts_cost_elevenlabs;
          
          if (currentTtsChars !== ttsChars || 
              currentTtsCostAzure !== ttsCostAzure || 
              currentTtsCostElevenlabs !== ttsCostElevenlabs) {
            updatedSpineData.book.tts_chars = ttsChars;
            updatedSpineData.book.tts_cost_azure = ttsCostAzure;
            updatedSpineData.book.tts_cost_elevenlabs = ttsCostElevenlabs;
            spineNeedsSave = true;
          }
        }
        
        // Save spine.json if it changed
        if (spineNeedsSave) {
          const currentSpineHash = createHash(updatedSpineData);
          if (originalSpineHash && currentSpineHash !== originalSpineHash) {
            const { FolderService } = await import('../services/FolderService');
            const folder = FolderService.getFolder();
            if (!folder) {
              throw new Error('Folder is required but not set');
            }
            const { authenticatedPut } = await import('../utils/api');
            const spineUrl = FolderService.addFolderToUrl(`/api/books/${isbn}/spine`);
            const saveResponse = await authenticatedPut(spineUrl, { data: updatedSpineData });

            if (!saveResponse.ok) {
              throw new Error(`Failed to save spine data: ${saveResponse.statusText}`);
            }
            
            setSpineData(updatedSpineData);
            setOriginalSpineHash(currentSpineHash);
          }
        }
      }

      // Save left page if it has changes (including when all blocks were deleted)
      if (activeSpread.leftHasChanges && activeSpread.leftPageIndex !== null) {
        const leftPageIndex = activeSpread.leftPageIndex;
        const leftPageData = leftPageDataFromProps;
        const leftChanges: string[] = [];

        // Save blocks if changed (allow empty blocks array when last block deleted)
        if (leftPageData && leftWordData) {
          const wordsFilename = getWordDataStrategy(leftPageData);
          if (wordsFilename) {
            const updatedWordData = convertBlocksToWordData(leftWordData, leftBlocks);
            
            // Update pageType in words file if it exists
            if (leftPageData.pageType !== undefined) {
              if (!updatedWordData.page) {
                updatedWordData.page = {};
              }
              updatedWordData.page.pageType = leftPageData.pageType;
            }

            const { FolderService: FolderServiceLeft } = await import('../services/FolderService');
            const folderLeft = FolderServiceLeft.getFolder();
            if (!folderLeft) {
              throw new Error('Folder is required but not set');
            }
            const { authenticatedPut } = await import('../utils/api');
            const leftPageUrl = FolderServiceLeft.getPageJsonUrl(isbn, wordsFilename);
            const saveResponse = await authenticatedPut(leftPageUrl, {
              pageIndex: leftPageIndex,
              data: updatedWordData
            });

            if (!saveResponse.ok) {
              const errorData = await saveResponse.json().catch(() => ({}));
              throw new Error(`Failed to save word data for left page: ${saveResponse.statusText} - ${errorData.error || 'Unknown error'}`);
            }

            // Detect all types of changes by comparing with original blocks
            const originalBlocksMap = new Map(
              leftOriginalBlocks.map(block => [block.blockId, block])
            );
            
            // Count word text changes (separate from new words)
            const wordsTextEdited = leftBlocks.reduce((count, block) => {
              const originalBlock = originalBlocksMap.get(block.blockId);
              if (originalBlock) {
                const originalWordsMap = new Map(
                  originalBlock.words.map(word => [word.wordId, word])
                );
                return count + block.words.filter(word => {
                  const originalWord = originalWordsMap.get(word.wordId);
                  // Only count if word existed and text changed
                  return originalWord && originalWord.text !== word.text;
                }).length;
              }
              return count;
            }, 0);

            // Count new words (words that didn't exist before)
            const wordsAdded = leftBlocks.reduce((count, block) => {
              const originalBlock = originalBlocksMap.get(block.blockId);
              if (originalBlock) {
                const originalWordsMap = new Map(
                  originalBlock.words.map(word => [word.wordId, word])
                );
                return count + block.words.filter(word => {
                  return !originalWordsMap.has(word.wordId);
                }).length;
              } else {
                // New block - all words are new
                return count + block.words.length;
              }
            }, 0);

            // Count word position changes (only for existing words, not new ones)
            const wordsPositionChanged = leftBlocks.reduce((count, block) => {
              const originalBlock = originalBlocksMap.get(block.blockId);
              if (originalBlock) {
                const originalWordsMap = new Map(
                  originalBlock.words.map(word => [word.wordId, word])
                );
                return count + block.words.filter(word => {
                  const originalWord = originalWordsMap.get(word.wordId);
                  if (!originalWord) return false; // New word, don't count as position change
                  const bb = word.boundingBox;
                  const origBb = originalWord.boundingBox;
                  return bb.x !== origBb.x || bb.y !== origBb.y || 
                         bb.width !== origBb.width || bb.height !== origBb.height;
                }).length;
              }
              return count; // New block, no position changes
            }, 0);

            // Count block changes
            const blocksChanged = leftBlocks.filter(block => {
              const originalBlock = originalBlocksMap.get(block.blockId);
              if (!originalBlock) return true; // New block
              
              // Check block type
              if (block.blockType !== originalBlock.blockType) return true;
              
              // Check readBlock
              if (block.readBlock !== originalBlock.readBlock) return true;
              
              // Check block position
              const bb = block.boundingBox;
              const origBb = originalBlock.boundingBox;
              if (bb.x !== origBb.x || bb.y !== origBb.y || 
                  bb.width !== origBb.width || bb.height !== origBb.height) {
                return true;
              }
              
              // Check if words were added/removed
              if (block.words.length !== originalBlock.words.length) return true;
              
              return false;
            }).length;

            // Count deleted blocks
            const currentBlockIds = new Set(leftBlocks.map(b => b.blockId));
            const deletedBlocks = leftOriginalBlocks.filter(b => !currentBlockIds.has(b.blockId)).length;

            if (wordsTextEdited > 0) {
              leftChanges.push(`${wordsTextEdited} word${wordsTextEdited !== 1 ? 's' : ''} text updated`);
            }
            if (wordsAdded > 0) {
              leftChanges.push(`${wordsAdded} word${wordsAdded !== 1 ? 's' : ''} added`);
            }
            if (wordsPositionChanged > 0) {
              leftChanges.push(`${wordsPositionChanged} word${wordsPositionChanged !== 1 ? 's' : ''} position${wordsPositionChanged !== 1 ? 's' : ''} updated`);
            }
            if (blocksChanged > 0) {
              leftChanges.push(`${blocksChanged} block${blocksChanged !== 1 ? 's' : ''} updated`);
            }
            if (deletedBlocks > 0) {
              leftChanges.push(`${deletedBlocks} block${deletedBlocks !== 1 ? 's' : ''} deleted`);
            }
            if (spineNeedsSave && leftPageData.pageType !== undefined) {
              leftChanges.push('page type changed');
            }
            
            // Reset original blocks and pageType after save
            setLeftOriginalBlocks(JSON.parse(JSON.stringify(leftBlocks)));
            if (leftPageDataFromProps?.pageType !== undefined) {
              setLeftOriginalPageType(leftPageDataFromProps.pageType as PageType);
            }
          }
        }

        if (leftChanges.length > 0) {
          leftPageChanges.push(...leftChanges);
        } else if (activeSpread.leftPageIndex !== null) {
          leftPageChanges.push('no changes');
        }
      } else if (activeSpread.leftPageIndex !== null) {
        leftPageChanges.push('no changes');
      }

      // Save right page if it has changes (including when all blocks were deleted)
      if (activeSpread.rightHasChanges && activeSpread.rightPageIndex !== null) {
        const rightPageIndex = activeSpread.rightPageIndex;
        const rightPageData = rightPageDataFromProps;
        const rightChanges: string[] = [];

        // Save blocks if changed (allow empty blocks array when last block deleted)
        if (rightPageData && rightWordData) {
          const wordsFilename = getWordDataStrategy(rightPageData);
          if (wordsFilename) {
            const updatedWordData = convertBlocksToWordData(rightWordData, rightBlocks);
            
            // Update pageType in words file if it exists
            if (rightPageData.pageType !== undefined) {
              if (!updatedWordData.page) {
                updatedWordData.page = {};
              }
              updatedWordData.page.pageType = rightPageData.pageType;
            }

            const { FolderService: FolderServiceRight } = await import('../services/FolderService');
            const folderRight = FolderServiceRight.getFolder();
            if (!folderRight) {
              throw new Error('Folder is required but not set');
            }
            const { authenticatedPut } = await import('../utils/api');
            const rightPageUrl = FolderServiceRight.getPageJsonUrl(isbn, wordsFilename);
            const saveResponse = await authenticatedPut(rightPageUrl, {
              pageIndex: rightPageIndex,
              data: updatedWordData
            });

            if (!saveResponse.ok) {
              const errorData = await saveResponse.json().catch(() => ({}));
              throw new Error(`Failed to save word data for right page: ${saveResponse.statusText} - ${errorData.error || 'Unknown error'}`);
            }

            // Detect all types of changes by comparing with original blocks
            const originalBlocksMap = new Map(
              rightOriginalBlocks.map(block => [block.blockId, block])
            );
            
            // Count word text changes (separate from new words)
            const wordsTextEdited = rightBlocks.reduce((count, block) => {
              const originalBlock = originalBlocksMap.get(block.blockId);
              if (originalBlock) {
                const originalWordsMap = new Map(
                  originalBlock.words.map(word => [word.wordId, word])
                );
                return count + block.words.filter(word => {
                  const originalWord = originalWordsMap.get(word.wordId);
                  // Only count if word existed and text changed
                  return originalWord && originalWord.text !== word.text;
                }).length;
              }
              return count;
            }, 0);

            // Count new words (words that didn't exist before)
            const wordsAdded = rightBlocks.reduce((count, block) => {
              const originalBlock = originalBlocksMap.get(block.blockId);
              if (originalBlock) {
                const originalWordsMap = new Map(
                  originalBlock.words.map(word => [word.wordId, word])
                );
                return count + block.words.filter(word => {
                  return !originalWordsMap.has(word.wordId);
                }).length;
              } else {
                // New block - all words are new
                return count + block.words.length;
              }
            }, 0);

            // Count word position changes
            const wordsPositionChanged = rightBlocks.reduce((count, block) => {
              const originalBlock = originalBlocksMap.get(block.blockId);
              if (originalBlock) {
                const originalWordsMap = new Map(
                  originalBlock.words.map(word => [word.wordId, word])
                );
                return count + block.words.filter(word => {
                  const originalWord = originalWordsMap.get(word.wordId);
                  if (!originalWord) return false;
                  const bb = word.boundingBox;
                  const origBb = originalWord.boundingBox;
                  return bb.x !== origBb.x || bb.y !== origBb.y || 
                         bb.width !== origBb.width || bb.height !== origBb.height;
                }).length;
              }
              return count;
            }, 0);

            // Count block changes
            const blocksChanged = rightBlocks.filter(block => {
              const originalBlock = originalBlocksMap.get(block.blockId);
              if (!originalBlock) return true; // New block
              
              // Check block type
              if (block.blockType !== originalBlock.blockType) return true;
              
              // Check readBlock
              if (block.readBlock !== originalBlock.readBlock) return true;
              
              // Check block position
              const bb = block.boundingBox;
              const origBb = originalBlock.boundingBox;
              if (bb.x !== origBb.x || bb.y !== origBb.y || 
                  bb.width !== origBb.width || bb.height !== origBb.height) {
                return true;
              }
              
              // Check if words were added/removed
              if (block.words.length !== originalBlock.words.length) return true;
              
              return false;
            }).length;

            // Count deleted blocks
            const currentBlockIds = new Set(rightBlocks.map(b => b.blockId));
            const deletedBlocks = rightOriginalBlocks.filter(b => !currentBlockIds.has(b.blockId)).length;

            if (wordsTextEdited > 0) {
              rightChanges.push(`${wordsTextEdited} word${wordsTextEdited !== 1 ? 's' : ''} text updated`);
            }
            if (wordsAdded > 0) {
              rightChanges.push(`${wordsAdded} word${wordsAdded !== 1 ? 's' : ''} added`);
            }
            if (wordsPositionChanged > 0) {
              rightChanges.push(`${wordsPositionChanged} word${wordsPositionChanged !== 1 ? 's' : ''} position${wordsPositionChanged !== 1 ? 's' : ''} updated`);
            }
            if (blocksChanged > 0) {
              rightChanges.push(`${blocksChanged} block${blocksChanged !== 1 ? 's' : ''} updated`);
            }
            if (deletedBlocks > 0) {
              rightChanges.push(`${deletedBlocks} block${deletedBlocks !== 1 ? 's' : ''} deleted`);
            }
            if (spineNeedsSave && rightPageData.pageType !== undefined) {
              rightChanges.push('page type changed');
            }
            
            // Reset original blocks and pageType after save
            setRightOriginalBlocks(JSON.parse(JSON.stringify(rightBlocks)));
            if (rightPageDataFromProps?.pageType !== undefined) {
              setRightOriginalPageType(rightPageDataFromProps.pageType as PageType);
            }
          }
        }

        if (rightChanges.length > 0) {
          rightPageChanges.push(...rightChanges);
        } else if (activeSpread.rightPageIndex !== null) {
          rightPageChanges.push('no changes');
        }
      } else if (activeSpread.rightPageIndex !== null) {
        rightPageChanges.push('no changes');
      }

      // Build formatted message with separate lines for book changes, left page, and right page
      // Always show all three lines, explicitly saying "none" if no changes
      const bookChangesLine = bookChanges.length > 0 ? `Book changes: ${bookChanges.join(', ')}` : 'Book changes: none';
      const leftPageLine = leftPageChanges.length > 0 ? `Left page: ${leftPageChanges.join(', ')}` : 'Left page: none';
      const rightPageLine = rightPageChanges.length > 0 ? `Right page: ${rightPageChanges.join(', ')}` : 'Right page: none';
      
      const formattedMessages = [bookChangesLine, leftPageLine, rightPageLine].join('\n');

      // Reset change flags after successful save
      setActiveSpread(prev => ({
        ...prev,
        leftHasChanges: false,
        rightHasChanges: false,
      }));

      // Reset original notes after successful save
      if (hasBookChanges) {
        setOriginalApprovalNotes(currentApproval?.notes || '');
      }

      // Save notes to all-books via approval API (if not already saved)
      if (hasBookChanges) {
        try {
          await onApprovalChange(currentApproval);
        } catch (error) {
          console.error('Error saving approval (notes) to all-books:', error);
          // Don't throw - spine save might have succeeded
        }
      }

      // Use red background when nothing was saved; green when something was saved
      const nothingSaved = bookChanges.length === 0
        && leftPageChanges.filter(x => x !== 'no changes').length === 0
        && rightPageChanges.filter(x => x !== 'no changes').length === 0;
      const toastContent = (
        <div style={{ whiteSpace: 'pre-line' }}>
          {bookChangesLine}
          {'\n'}
          {leftPageLine}
          {'\n'}
          {rightPageLine}
        </div>
      );
      if (nothingSaved) {
        toast(toastContent, {
          style: { background: '#dc2626', color: 'white' },
          icon: '⚠️',
        });
      } else {
        toast.success(toastContent);
      }
      
      // Move focus to book container so arrow keys work
      const pageContainer = document.querySelector('.page-container') as HTMLElement;
      if (pageContainer) {
        pageContainer.focus();
      }
      
      // Check if any words had adjusted IDs (collisions that were reassigned)
      // If so, force reload the page data to get the new IDs
      let needsReload = false;
      
      // Check left page
      if (activeSpread.leftHasChanges && activeSpread.leftPageIndex !== null) {
        const leftPageData = pages.find(p => p['page-index'] === activeSpread.leftPageIndex);
        if (leftPageData) {
          const leftBlocks = wordDataState.blocksByPage.get(activeSpread.leftPageIndex) || [];
          const hasAdjustedIds = leftBlocks.some(block => 
            block.words.some(word => isAdjustedId(word.wordId))
          );
          if (hasAdjustedIds) {
            needsReload = true;
            const wordsFilename = getWordDataStrategy(leftPageData);
            if (wordsFilename) {
              await wordDataActions.loadPageBlocks(activeSpread.leftPageIndex, isbn, wordsFilename, true);
            }
          }
        }
      }
      
      // Check right page
      if (activeSpread.rightHasChanges && activeSpread.rightPageIndex !== null) {
        const rightPageData = pages.find(p => p['page-index'] === activeSpread.rightPageIndex);
        if (rightPageData) {
          const rightBlocks = wordDataState.blocksByPage.get(activeSpread.rightPageIndex) || [];
          const hasAdjustedIds = rightBlocks.some(block => 
            block.words.some(word => isAdjustedId(word.wordId))
          );
          if (hasAdjustedIds) {
            needsReload = true;
            const wordsFilename = getWordDataStrategy(rightPageData);
            if (wordsFilename) {
              await wordDataActions.loadPageBlocks(activeSpread.rightPageIndex, isbn, wordsFilename, true);
            }
          }
        }
      }
      
      // Recalculate TTS metadata after save
      await calculateBookCharCount();
    } catch (error) {
      console.error('[SPREAD EDITOR] Save error:', error);
      toast.error(`Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  }, [activeSpread, isbn, spineData, originalSpineHash, getWordDataStrategy, convertBlocksToWordData, hasBookChanges, currentApproval, onApprovalChange, wordDataState, wordDataActions, pages, ttsChars, ttsCostAzure, ttsCostElevenlabs, calculateBookCharCount]);

  // Keep refs in sync with current values
  useEffect(() => {
    activeSpreadRef.current = activeSpread;
    hasBookChangesRef.current = hasBookChanges;
    isSavingRef.current = isSaving;
  }, [activeSpread, hasBookChanges, isSaving]);
  
  // Keyboard shortcut: Cmd+S (Mac) or Ctrl+S (Windows/Linux) to save
  // Attached at window level, not tied to React lifecycle
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if Cmd+S (Mac) or Ctrl+S (Windows/Linux) is pressed
      const isSaveShortcut = (e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S');
      
      if (isSaveShortcut) {
        // Always prevent default to stop browser save dialog
        e.preventDefault();
        e.stopPropagation();
        
        // Check if save is active (has changes and not currently saving)
        // Read from refs to get the latest values
        const currentSpread = activeSpreadRef.current;
        const hasChanges = currentSpread.leftHasChanges || currentSpread.rightHasChanges || hasBookChangesRef.current;
        
        if (hasChanges && !isSavingRef.current) {
          handleSave();
        }
      }
    };

    // Attach at window level for global keyboard shortcuts
    console.log('[CMD+S] Attaching window event listener');
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      console.log('[CMD+S] Removing window event listener');
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleSave]); // Only depend on handleSave, read everything else from refs

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    setEditMode: handleSetEditMode,
    handleSave,
    getHasChanges: () => activeSpread.leftHasChanges || activeSpread.rightHasChanges || hasBookChanges,
    getIsSaving: () => isSaving,
    getSaveButtonText: () => {
      if (isSaving) return 'Saving...';
      if (activeSpread.leftHasChanges && activeSpread.rightHasChanges) return 'Save Both Pages';
      if (activeSpread.leftHasChanges || activeSpread.rightHasChanges || hasBookChanges) return 'Save Changes';
      return 'No Changes';
    },
    getSaveButtonTitle: () => {
      if (isSaving) return 'Saving...';
      if (activeSpread.leftHasChanges && activeSpread.rightHasChanges) return 'Save changes to both pages';
      if (activeSpread.leftHasChanges) return 'Save changes to left page';
      if (activeSpread.rightHasChanges) return 'Save changes to right page';
      if (hasBookChanges) return 'Save notes';
      return 'No changes to save';
    },
    getCurrentApproval: () => currentApproval,
    handleNotesChange,
    getShowOverlays: () => showOverlays,
    getShowBlockBg: () => showBlockBg,
    getInvertPage: () => invertPage,
    handleToggleOverlays,
    handleToggleBlockBg,
    handleToggleInvert,
    handleAddBlock,
    getLeftPageIndex: () => activeSpread.leftPageIndex,
    getRightPageIndex: () => activeSpread.rightPageIndex,
    cancelMerge,
    getAudioService: () => audioService,
    getSelectedVoice: () => selectedVoice,
    getElevenlabsModel: () => elevenlabsModel,
    isBlockEditing: () => editingLeftBlock !== null || editingRightBlock !== null,
    closeAllBlockEditors,
  }), [handleSetEditMode, handleSave, activeSpread, hasBookChanges, isSaving, currentApproval, handleNotesChange, showOverlays, showBlockBg, invertPage, handleToggleOverlays, handleToggleBlockBg, handleToggleInvert, handleAddBlock, cancelMerge, audioService, selectedVoice, elevenlabsModel, editingLeftBlock, editingRightBlock, closeAllBlockEditors]);

  return (
    <div 
      className="approval-status spread-editor" 
      style={{ 
        position: 'relative',
        width: '100%',
      }}
    >
      <div 
        ref={containerRef}
        className="approval-container"
        style={{
          maxHeight: '100vh',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {/* Content */}
        <>
        {/* Toggle Buttons */}
        <div style={{ padding: '8px', borderBottom: '1px solid var(--border-primary)' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'center' }}>
            <div className="compact-toggle">
              <label className="compact-toggle-label">
                <span className="compact-toggle-text">Overlays</span>
                <div className="compact-toggle-switch">
                  <input
                    type="checkbox"
                    checked={showOverlays}
                    onChange={handleToggleOverlays}
                    className="compact-toggle-input"
                    id="spread-overlay-toggle"
                  />
                  <span className="compact-toggle-slider"></span>
                </div>
              </label>
            </div>
            <div className="compact-toggle">
              <label className="compact-toggle-label">
                <span className="compact-toggle-text">Block Bg</span>
                <div className="compact-toggle-switch">
                  <input
                    type="checkbox"
                    checked={showBlockBg}
                    onChange={handleToggleBlockBg}
                    className="compact-toggle-input"
                    id="spread-block-bg-toggle"
                  />
                  <span className="compact-toggle-slider"></span>
                </div>
              </label>
            </div>
            <div className="compact-toggle">
              <label className="compact-toggle-label">
                <span className="compact-toggle-text">Invert</span>
                <div className="compact-toggle-switch">
                  <input
                    type="checkbox"
                    checked={invertPage}
                    onChange={handleToggleInvert}
                    className="compact-toggle-input"
                    id="spread-invert-toggle"
                  />
                  <span className="compact-toggle-slider"></span>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Audio Service Selector */}
        <div style={{ padding: '8px', borderBottom: '1px solid var(--border-primary)' }}>
          {/* First row: Speaker, Play, Stop, Apply to book */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px' }}>            
            {/* Play Button */}
            {onReadMe && (
              <button
                className={`tts-play-button ${ttsActive ? 'active' : ''} ${ttsLoading ? 'tts-loading' : ''}`}
                onClick={onReadMe}
                disabled={ttsLoading}
                title={
                  ttsLoading 
                    ? 'Loading audio...' 
                    : ttsPaused 
                      ? 'Resume reading' 
                      : ttsActive 
                        ? 'Pause reading' 
                        : 'Play audio'
                }
                style={getButtonStyle({
                  minWidth: '65px',
                  width: 'auto',
                  cursor: ttsLoading ? 'not-allowed' : 'pointer',
                })}
              >
                {ttsLoading 
                  ? '⏳' 
                  : ttsPaused 
                    ? '▶️ Resume' 
                    : ttsActive 
                      ? '⏸️ Pause' 
                      : '▶️ Play'}
              </button>
            )}
            
            {/* Stop Button */}
            {onStop && (
              <button
                className={`tts-stop-button ${ttsActive || ttsPaused ? 'active' : ''}`}
                onClick={onStop}
                disabled={!ttsActive && !ttsPaused}
                title="Stop audio"
                style={getButtonStyle({
                  minWidth: '65px',
                  width: 'auto',
                  cursor: (!ttsActive && !ttsPaused) ? 'not-allowed' : 'pointer',
                })}
              >
                ⏹️ Stop
              </button>
            )}

            {/* Apply to book button */}
            <button
              onClick={handleApplyVoiceSettings}
              disabled={!voiceSettingsChanged}
              style={getButtonStyle({
                border: voiceSettingsChanged ? '1px solid #dc2626' : '1px solid var(--border-primary)',
                backgroundColor: voiceSettingsChanged ? '#dc2626' : 'var(--bg-secondary)',
                color: voiceSettingsChanged ? 'white' : 'var(--text-secondary)',
                cursor: voiceSettingsChanged ? 'pointer' : 'not-allowed',
                opacity: voiceSettingsChanged ? 1 : 0.6,
              })}
              title={voiceSettingsChanged ? 'Save voice settings to book' : 'No changes to save'}
            >
              Apply voice to book
            </button>

            {/* Use custom voice toggle - only when book has audio_inside */}
            {spineData?.book?.audio_inside === true && (
              <label className="custom-voice-toggle" style={{ display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer', fontSize: '11px', color: 'var(--text-primary)' }}>
                <input
                  type="checkbox"
                  checked={audioService === 'curioso-custom'}
                  onChange={(e) => handleUseCustomVoiceChange(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                Custom Voice
              </label>
            )}
          </div>

          {/* Second row: Azure|ElevenLabs toggle, Voice dropdown */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px' }}>
            {/* Service Toggle (Azure / ElevenLabs - inactive when Use custom voice is on) */}
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <button
                onClick={() => handleAudioServiceChange('azure')}
                style={getButtonStyle({
                  minWidth: '65px',
                  width: 'auto',
                  border: `1px solid ${audioService === 'azure' ? 'var(--accent-primary)' : 'var(--border-primary)'}`,
                  backgroundColor: audioService === 'azure' ? 'var(--accent-primary)' : 'var(--bg-secondary)',
                  color: audioService === 'azure' ? 'white' : 'var(--text-primary)',
                })}
              >
                Azure
              </button>
              <button
                onClick={() => handleAudioServiceChange('elevenlabs')}
                style={getButtonStyle({
                  minWidth: '65px',
                  width: 'auto',
                  border: `1px solid ${audioService === 'elevenlabs' ? 'var(--accent-primary)' : 'var(--border-primary)'}`,
                  backgroundColor: audioService === 'elevenlabs' ? 'var(--accent-primary)' : 'var(--bg-secondary)',
                  color: audioService === 'elevenlabs' ? 'white' : 'var(--text-primary)',
                })}
              >
                ElevenLabs
              </button>
            </div>

            {/* Voice Dropdown (when curioso-custom, show "default" only) */}
            {audioService === 'curioso-custom' ? (
              <select
                value="default"
                disabled
                style={{
                  padding: '4px 8px',
                  border: '1px solid var(--border-primary)',
                  borderRadius: '4px',
                  fontSize: '11px',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  cursor: 'default',
                }}
              >
                <option value="default">default</option>
              </select>
            ) : audioService === 'azure' ? (
              <select
                value={selectedVoice}
                onChange={(e) => handleVoiceChange(e.target.value)}
                style={{
                  padding: '4px 8px',
                  border: '1px solid var(--border-primary)',
                  borderRadius: '4px',
                  fontSize: '11px',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                }}
              >
                {AZURE_VOICES.map(voice => (
                  <option key={voice} value={voice}>
                    {voice}
                  </option>
                ))}
              </select>
            ) : (
              <select
                value={selectedVoice}
                onChange={(e) => handleVoiceChange(e.target.value)}
                disabled={loadingElevenlabsVoices}
                style={{
                  padding: '4px 8px',
                  border: '1px solid var(--border-primary)',
                  borderRadius: '4px',
                  fontSize: '11px',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  cursor: loadingElevenlabsVoices ? 'not-allowed' : 'pointer',
                  opacity: loadingElevenlabsVoices ? 0.6 : 1,
                }}
              >
                {loadingElevenlabsVoices ? (
                  <option value="">Loading voices...</option>
                ) : elevenlabsVoices.length === 0 ? (
                  <option value="">No voices available</option>
                ) : (
                  <>
                    <option value="">Select voice...</option>
                    {elevenlabsVoices.map(voice => (
                      <option key={voice.voice_id || voice.name} value={voice.name}>
                        {voice.name}
                      </option>
                    ))}
                  </>
                )}
              </select>
            )}
          </div>

          {/* Third row: Character count and cost estimate (full access only) */}
          {isFullAccessUser && bookCharCount !== null && ttsCostAzure !== null && ttsCostElevenlabs !== null && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              padding: '6px 12px',
              backgroundColor: 'var(--bg-tertiary, rgba(0, 0, 0, 0.05))',
              borderRadius: '4px',
              border: '1px solid var(--border-primary)',
            }}>
              <span style={{ 
                fontSize: '0.75rem', 
                color: 'var(--text-secondary)',
                whiteSpace: 'nowrap',
                fontWeight: 500,
              }}>
                {bookCharCount.toLocaleString()} chars : Azure ~${ttsCostAzure.toFixed(2)} : Elevenlabs ~${ttsCostElevenlabs.toFixed(2)}
              </span>
            </div>
          )}
        </div>

        {/* Notes Textarea */}
        <div style={{ padding: '8px', borderBottom: '1px solid var(--border-primary)' }}>
          <textarea
            value={currentApproval?.notes || ''}
            onChange={(e) => handleNotesChange(e.target.value)}
            placeholder="Notes..."
            rows={4}
            style={{
              width: '100%',
              padding: '6px',
              border: '1px solid var(--border-primary)',
              borderRadius: '4px',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              fontSize: '12px',
              fontFamily: 'inherit',
              resize: 'vertical',
              minHeight: '80px',
            }}
          />
        </div>

        {/* Left Page Section */}
        {activeSpread.leftPageIndex !== null && leftPageDataFromProps && (
          <div style={{ padding: '8px', borderTop: '1px solid var(--border-primary)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: leftPageCollapsed ? '0' : '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>
                <span>Left Page ({activeSpread.leftPageIndex})</span>
                <InlinePageTypeEditor
                  pageType={(leftPageDataFromProps?.pageType || '') as PageType}
                  pageIndex={activeSpread.leftPageIndex}
                  pageLabel="Left"
                  onChange={handleLeftPageTypeChange}
                  getPageTypeColor={getPageTypeColor}
                  getPageTypeDisplay={getPageTypeDisplay}
                />
                <button
                  onClick={() => handleRemoveBlockReads(activeSpread.leftPageIndex, true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    color: 'var(--text-secondary)',
                  }}
                  title="Remove block reads (set all blocks to not read)"
                >
                  <VolumeX size={16} />
                </button>
                <button
                  onClick={() => handleAddBlock(activeSpread.leftPageIndex, true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    color: 'var(--text-secondary)',
                  }}
                  title="Add Block"
                >
                  <Plus size={16} />
                </button>
              </div>
              <button
                onClick={() => setLeftPageCollapsed(!leftPageCollapsed)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--text-secondary)',
                }}
                title={leftPageCollapsed ? 'Expand' : 'Collapse'}
              >
                {leftPageCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              </button>
            </div>
            
            {/* Left Page Blocks */}
            {!leftPageCollapsed && (
              <>
                {editingLeftBlock ? (
              <TextractBlockEditor
                block={editingLeftBlock}
                imageDimensions={leftWordData?.image_dimensions!}
                onCancel={() => handleBlockCancel(true)}
                onHighlightBlock={handleHighlightBlock}
                onClearHighlight={handleClearHighlight}
                allBlocks={leftBlocks}
                onBlockChange={(updatedBlock) => handleBlockUpdateFromEditor(updatedBlock, true)}
                onBlockDelete={(blockId) => {
                  if (activeSpread.leftPageIndex !== null) {
                    wordDataActions.deleteBlock(activeSpread.leftPageIndex, blockId);
                  }
                }}
                onReadFromWord={onReadFromWord ? (wordId) => onReadFromWord(wordId, currentLeftPage, true) : undefined}
                pageIndex={currentLeftPage}
              />
            ) : (
              leftBlocks.length > 0 && (
                <div className="blocks-list" style={{ marginTop: '8px' }}>
                  {leftBlocks.map((block) => (
                    <div 
                      key={block.blockId} 
                      className="block-item"
                      ref={(el) => {
                        if (el) {
                          const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
                          const pageIndex = activeSpread.leftPageIndex;
                          if (pageIndex !== null) {
                            blockItemRefs.current.set(`${pageIndex}-${blockIdNum}`, el);
                          }
                        } else {
                          const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
                          const pageIndex = activeSpread.leftPageIndex;
                          if (pageIndex !== null) {
                            blockItemRefs.current.delete(`${pageIndex}-${blockIdNum}`);
                          }
                        }
                      }}
                      style={{
                        transform: (() => {
                          const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
                          const dragId = typeof reorderDragBlockId === 'string' ? parseInt(reorderDragBlockId, 10) : reorderDragBlockId;
                          const isOnCorrectPage = reorderDragIsLeft && activeSpread.leftPageIndex !== null;
                          return dragId === blockIdNum && isReorderingBlock && isOnCorrectPage
                            ? `translateY(${reorderDragOffset}px)` 
                            : undefined;
                        })(),
                        opacity: (() => {
                          const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
                          const dragId = typeof reorderDragBlockId === 'string' ? parseInt(reorderDragBlockId, 10) : reorderDragBlockId;
                          const isOnCorrectPage = reorderDragIsLeft && activeSpread.leftPageIndex !== null;
                          return dragId === blockIdNum && isReorderingBlock && isOnCorrectPage ? 0.5 : 1;
                        })(),
                        zIndex: (() => {
                          const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
                          const dragId = typeof reorderDragBlockId === 'string' ? parseInt(reorderDragBlockId, 10) : reorderDragBlockId;
                          const isOnCorrectPage = reorderDragIsLeft && activeSpread.leftPageIndex !== null;
                          return dragId === blockIdNum && isReorderingBlock && isOnCorrectPage ? 1000 : 1;
                        })(),
                      }}
                    >
                      <div className="block-header">
                        <div className="block-header-row-1" style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                            <div 
                              className="block-drag-handle"
                              onMouseDown={(e) => {
                                const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
                                handleBlockReorderMouseDown(e, blockIdNum, true);
                              }}
                              title="Drag to reorder blocks"
                            >
                              ⋮⋮
                            </div>
                            <select
                              className="block-type-dropdown"
                              value={block.blockType || ''}
                              onChange={(e) => {
                                e.stopPropagation();
                                const updatedBlock = { ...block, blockType: e.target.value as any };
                                handleLeftBlockUpdate(updatedBlock);
                              }}
                            >
                              <option value="">-- No Block Type --</option>
                              {BLOCK_TYPES.map(type => (
                                <option key={type} value={type}>
                                  {type.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </option>
                              ))}
                            </select>
                            <label className="read-block-toggle" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span className="toggle-label">Read</span>
                              <input
                                type="checkbox"
                                checked={block.readBlock || false}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  const updatedBlock = { ...block, readBlock: e.target.checked };
                                  handleLeftBlockUpdate(updatedBlock);
                                }}
                              />
                            </label>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
                              handleBlockMerge(blockIdNum, activeSpread.leftPageIndex, true);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              color: (() => {
                                const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
                                const sourceId = typeof mergeSourceBlockId === 'string' ? parseInt(mergeSourceBlockId, 10) : mergeSourceBlockId;
                                // Green if merge is active and this is not the source block
                                if (mergeSourceBlockId !== null && mergeSourcePageIndex === activeSpread.leftPageIndex && blockIdNum !== sourceId) {
                                  return '#10b981'; // Green color
                                }
                                return 'var(--text-secondary)';
                              })(),
                            }}
                            title={(() => {
                              const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
                              const sourceId = typeof mergeSourceBlockId === 'string' ? parseInt(mergeSourceBlockId, 10) : mergeSourceBlockId;
                              if (mergeSourceBlockId !== null && mergeSourcePageIndex === activeSpread.leftPageIndex) {
                                if (blockIdNum === sourceId) {
                                  return 'Click to cancel merge or select another block to merge into';
                                }
                                return 'Click to merge this block with the selected block';
                              }
                              return 'Merge block - Click to select this block, then click another to merge';
                            })()}
                          >
                            <GitMerge size={14} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
                              if (confirm(`Delete block ${blockIdNum}?`)) {
                                wordDataActions.deleteBlock(activeSpread.leftPageIndex!, blockIdNum);
                              }
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              color: 'var(--text-secondary)',
                            }}
                            title="Delete block"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                      
                      {/* Block Words */}
                      <div className="block-words">
                        <div className="words-badges">
                          {block.words?.map((word: any, wordIndex: number) => {
                            const selectedWordId = wordDataState.selectedWord?.wordId;
                            const selectedPageIndex = wordDataState.selectedWord?.pageIndex;
                            const flashingWordId = wordDataState.flashingWord?.wordId;
                            const flashingPageIndex = wordDataState.flashingWord?.pageIndex;
                            const currentWordIdStr = String(word.wordId);
                            const isSelected = selectedWordId === currentWordIdStr && selectedPageIndex === activeSpread.leftPageIndex;
                            const isFlashing = flashingWordId === currentWordIdStr && flashingPageIndex === activeSpread.leftPageIndex;
                            
                            // Debug logging - log whenever flashingWord exists
                            if (flashingWordId) {
                              console.log('[SpreadEditor Left] Flash check:', {
                                wordId: word.wordId,
                                wordIdStr: currentWordIdStr,
                                leftPageIndex: activeSpread.leftPageIndex,
                                flashingWordId,
                                flashingPageIndex,
                                wordIdMatch: flashingWordId === currentWordIdStr,
                                pageIndexMatch: flashingPageIndex === activeSpread.leftPageIndex,
                                isFlashing
                              });
                            }
                            
                            return (
                              <span 
                                key={wordIndex} 
                                className={`word-badge ${isSelected ? 'selected-word' : ''} ${isFlashing ? 'flashing-word' : ''}`}
                                onClick={() => handleBlockEdit(block, true)}
                                style={{ 
                                  cursor: 'pointer'
                                }}
                              >
                                <span className="word-text">{word.text}</span>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
                {leftBlocks.length === 0 && !spreadData.loadingLeft && (
                  <div style={{ fontSize: '10px', color: 'var(--text-secondary)', fontStyle: 'italic', marginTop: '4px' }}>
                    No blocks found
                  </div>
                )}
                {spreadData.loadingLeft && (
                  <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    Loading blocks...
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Right Page Section */}
        {activeSpread.rightPageIndex !== null && rightPageDataFromProps && (
          <div style={{ padding: '8px', borderTop: '1px solid var(--border-primary)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: rightPageCollapsed ? '0' : '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>
                <span>Right Page ({activeSpread.rightPageIndex})</span>
                <InlinePageTypeEditor
                  pageType={(rightPageDataFromProps?.pageType || '') as PageType}
                  pageIndex={activeSpread.rightPageIndex}
                  pageLabel="Right"
                  onChange={handleRightPageTypeChange}
                  getPageTypeColor={getPageTypeColor}
                  getPageTypeDisplay={getPageTypeDisplay}
                />
                <button
                  onClick={() => handleRemoveBlockReads(activeSpread.rightPageIndex, false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    color: 'var(--text-secondary)',
                  }}
                  title="Remove block reads (set all blocks to not read)"
                >
                  <VolumeX size={16} />
                </button>
                <button
                  onClick={() => handleAddBlock(activeSpread.rightPageIndex, false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    color: 'var(--text-secondary)',
                  }}
                  title="Add Block"
                >
                  <Plus size={16} />
                </button>
              </div>
              <button
                onClick={() => setRightPageCollapsed(!rightPageCollapsed)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--text-secondary)',
                }}
                title={rightPageCollapsed ? 'Expand' : 'Collapse'}
              >
                {rightPageCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              </button>
            </div>
            
            {/* Right Page Blocks */}
            {!rightPageCollapsed && (
              <>
                {editingRightBlock ? (
              <TextractBlockEditor
                block={editingRightBlock}
                imageDimensions={rightWordData?.image_dimensions!}
                onCancel={() => handleBlockCancel(false)}
                onHighlightBlock={handleHighlightBlock}
                onClearHighlight={handleClearHighlight}
                allBlocks={rightBlocks}
                onBlockChange={(updatedBlock) => handleBlockUpdateFromEditor(updatedBlock, false)}
                onBlockDelete={(blockId) => {
                  if (activeSpread.rightPageIndex !== null) {
                    wordDataActions.deleteBlock(activeSpread.rightPageIndex, blockId);
                  }
                }}
                onReadFromWord={onReadFromWord ? (wordId) => onReadFromWord(wordId, currentRightPage, false) : undefined}
                pageIndex={currentRightPage}
              />
            ) : (
              rightBlocks.length > 0 && (
                <div className="blocks-list" style={{ marginTop: '8px' }}>
                  {rightBlocks.map((block) => (
                    <div 
                      key={block.blockId} 
                      className="block-item"
                      ref={(el) => {
                        if (el) {
                          const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
                          const pageIndex = activeSpread.rightPageIndex;
                          if (pageIndex !== null) {
                            blockItemRefs.current.set(`${pageIndex}-${blockIdNum}`, el);
                          }
                        } else {
                          const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
                          const pageIndex = activeSpread.rightPageIndex;
                          if (pageIndex !== null) {
                            blockItemRefs.current.delete(`${pageIndex}-${blockIdNum}`);
                          }
                        }
                      }}
                      style={{
                        transform: (() => {
                          const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
                          const dragId = typeof reorderDragBlockId === 'string' ? parseInt(reorderDragBlockId, 10) : reorderDragBlockId;
                          const isOnCorrectPage = !reorderDragIsLeft && activeSpread.rightPageIndex !== null;
                          return dragId === blockIdNum && isReorderingBlock && isOnCorrectPage
                            ? `translateY(${reorderDragOffset}px)` 
                            : undefined;
                        })(),
                        opacity: (() => {
                          const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
                          const dragId = typeof reorderDragBlockId === 'string' ? parseInt(reorderDragBlockId, 10) : reorderDragBlockId;
                          const isOnCorrectPage = !reorderDragIsLeft && activeSpread.rightPageIndex !== null;
                          return dragId === blockIdNum && isReorderingBlock && isOnCorrectPage ? 0.5 : 1;
                        })(),
                        zIndex: (() => {
                          const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
                          const dragId = typeof reorderDragBlockId === 'string' ? parseInt(reorderDragBlockId, 10) : reorderDragBlockId;
                          const isOnCorrectPage = !reorderDragIsLeft && activeSpread.rightPageIndex !== null;
                          return dragId === blockIdNum && isReorderingBlock && isOnCorrectPage ? 1000 : 1;
                        })(),
                      }}
                    >
                      <div className="block-header">
                        <div className="block-header-row-1" style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                            <div 
                              className="block-drag-handle"
                              onMouseDown={(e) => {
                                const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
                                handleBlockReorderMouseDown(e, blockIdNum, false);
                              }}
                              title="Drag to reorder blocks"
                            >
                              ⋮⋮
                            </div>
                            <select
                              className="block-type-dropdown"
                              value={block.blockType || ''}
                              onChange={(e) => {
                                e.stopPropagation();
                                const updatedBlock = { ...block, blockType: e.target.value as any };
                                handleRightBlockUpdate(updatedBlock);
                              }}
                            >
                              <option value="">-- No Block Type --</option>
                              {BLOCK_TYPES.map(type => (
                                <option key={type} value={type}>
                                  {type.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </option>
                              ))}
                            </select>
                            <label className="read-block-toggle" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span className="toggle-label">Read</span>
                              <input
                                type="checkbox"
                                checked={block.readBlock || false}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  const updatedBlock = { ...block, readBlock: e.target.checked };
                                  handleRightBlockUpdate(updatedBlock);
                                }}
                              />
                            </label>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
                              handleBlockMerge(blockIdNum, activeSpread.rightPageIndex, false);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              color: (() => {
                                const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
                                const sourceId = typeof mergeSourceBlockId === 'string' ? parseInt(mergeSourceBlockId, 10) : mergeSourceBlockId;
                                // Green if merge is active and this is not the source block
                                if (mergeSourceBlockId !== null && mergeSourcePageIndex === activeSpread.rightPageIndex && blockIdNum !== sourceId) {
                                  return '#10b981'; // Green color
                                }
                                return 'var(--text-secondary)';
                              })(),
                            }}
                            title={(() => {
                              const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
                              const sourceId = typeof mergeSourceBlockId === 'string' ? parseInt(mergeSourceBlockId, 10) : mergeSourceBlockId;
                              if (mergeSourceBlockId !== null && mergeSourcePageIndex === activeSpread.rightPageIndex) {
                                if (blockIdNum === sourceId) {
                                  return 'Click to cancel merge or select another block to merge into';
                                }
                                return 'Click to merge this block with the selected block';
                              }
                              return 'Merge block - Click to select this block, then click another to merge';
                            })()}
                          >
                            <GitMerge size={14} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
                              if (confirm(`Delete block ${blockIdNum}?`)) {
                                wordDataActions.deleteBlock(activeSpread.rightPageIndex!, blockIdNum);
                              }
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              color: 'var(--text-secondary)',
                            }}
                            title="Delete block"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                      
                      {/* Block Words */}
                      <div className="block-words">
                        <div className="words-badges">
                          {block.words?.map((word: any, wordIndex: number) => {
                            const selectedWordId = wordDataState.selectedWord?.wordId;
                            const selectedPageIndex = wordDataState.selectedWord?.pageIndex;
                            const flashingWordId = wordDataState.flashingWord?.wordId;
                            const flashingPageIndex = wordDataState.flashingWord?.pageIndex;
                            const currentWordIdStr = String(word.wordId);
                            const isSelected = selectedWordId === currentWordIdStr && selectedPageIndex === activeSpread.rightPageIndex;
                            const isFlashing = flashingWordId === currentWordIdStr && flashingPageIndex === activeSpread.rightPageIndex;
                            
                            // Debug logging - log whenever flashingWord exists
                            if (flashingWordId) {
                              console.log('[SpreadEditor Right] Flash check:', {
                                wordId: word.wordId,
                                wordIdStr: currentWordIdStr,
                                rightPageIndex: activeSpread.rightPageIndex,
                                flashingWordId,
                                flashingPageIndex,
                                wordIdMatch: flashingWordId === currentWordIdStr,
                                pageIndexMatch: flashingPageIndex === activeSpread.rightPageIndex,
                                isFlashing
                              });
                            }
                            
                            return (
                              <span 
                                key={wordIndex} 
                                className={`word-badge ${isSelected ? 'selected-word' : ''} ${isFlashing ? 'flashing-word' : ''}`}
                                onClick={() => handleBlockEdit(block, false)}
                                style={{ 
                                  cursor: 'pointer'
                                }}
                              >
                                <span className="word-text">{word.text}</span>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
                {rightBlocks.length === 0 && !spreadData.loadingRight && (
                  <div style={{ fontSize: '10px', color: 'var(--text-secondary)', fontStyle: 'italic', marginTop: '4px' }}>
                    No blocks found
                  </div>
                )}
                {spreadData.loadingRight && (
                  <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    Loading blocks...
                  </div>
                )}
              </>
            )}
          </div>
        )}
        </>
      </div>
    </div>
  );
});

SpreadEditor.displayName = 'SpreadEditor';

export default SpreadEditor;

